import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "@/DesignStudio.css";
import PanicBackButton from "@/components/PanicBackButton";
import DesignGalleryModal from "@/components/dashboard/DesignGalleryModal";
import NspireWalkthrough from "@/components/NspireWalkthrough";
import TestRunBanner from "@/components/TestRunBanner";
import DynamicMaterialsBuilder from "@/components/DynamicMaterialsBuilder";
import FieldMode from "@/FieldMode";
import SystemDoctrine from "@/components/SystemDoctrine";

const BACKEND_URL = "";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c45d5dc5-1aa9-4fda-8179-43802ae25660/artifacts/r7bevoxt_1210928841187418.jpg";


// ─── Palette Data: Moovin Forward Design System ───

// Ceiling scope catalog — mirrors backend design_generator.CEILING_SCOPES.
// Each variation gets one of three ceiling tiers, deterministically derived
// from its id so a given variation always shows the same ceiling scope.
const CEILING_SCOPES = [
  {
    key: "baseline_flat_white",
    label: "Baseline · Flat White + Vent Refresh",
    tier: 1,
    detail: "Full ceiling wash + spot repair (nail pops, water rings). One coat SW ProMar Ceiling flat. Register + return grille refresh (paint or replace).",
    swColor: "SW 7005 Pure White",
    sheen: "Flat",
  },
  {
    key: "signature_skim_two_coat",
    label: "Signature · Popcorn Scrape + Two-Coat",
    tier: 2,
    detail: "Popcorn scrape (where present) + skim coat to smooth. Two coats SW ProMar Ceiling flat for uniform reflectance. Vent register hardware upgrade to match variation's hardware family.",
    swColor: "SW 7005 Pure White",
    sheen: "Flat",
  },
  {
    key: "executive_level4_skim",
    label: "Executive · Level-4 Skim + Soft Bedroom Tone",
    tier: 3,
    detail: "Level-4 skim finish (buttery-smooth ceiling — reads high-end even in a rental). Optional soft ceiling tone in bedrooms (~5% pigment shift from wall for depth). Crown molding refresh/paint if present.",
    swColor: "SW 7005 Pure White (public) · custom soft tone (bedrooms)",
    sheen: "Flat",
  },
];

// Deterministic ceiling scope for a variation id — simple djb2-style hash.
export const ceilingScopeFor = (id) => {
  const s = String(id || "");
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return CEILING_SCOPES[Math.abs(h) % CEILING_SCOPES.length];
};

const PALETTES = []; // PURGED (Feb 12, 2026): Legacy hardcoded MFC-01…MFC-17 palette
                     // array (color names, hardware brands, SW codes) fully
                     // removed per Doctrine v3.0. All material selection is
                     // routed through DynamicMaterialsBuilder — owner enters
                     // real brand · SKU · vendor · unit cost. No fixed labels,
                     // no locked hardware, no white-box terminology.

const CATEGORIES = [
  { key: "vanity", label: "Vanity / Cabinets", room: "Kitchen & Bath" },
  { key: "hardware", label: "Hardware & Pulls", room: "All Rooms" },
  { key: "flooring", label: "Flooring", room: "All Rooms" },
  { key: "countertop", label: "Countertop", room: "Kitchen & Bath" },
  { key: "wallPaint", label: "Wall Paint", room: "All Rooms" },
  { key: "accentPaint", label: "Accent Wall", room: "Feature Walls" },
  { key: "tile", label: "Tile & Backsplash", room: "Kitchen & Bath" },
  { key: "fixtures", label: "Light Fixtures", room: "All Rooms" }
];

const PEEL_STICK_OPTIONS = [
  { name: "Peel & Stick Vinyl Plank", desc: "Click-lock LVP. Installs in hours, swaps in minutes.", shop: true, tag: "Flooring" },
  { name: "Peel & Stick Backsplash Tile", desc: "Subway or mosaic. No grout. Instant refresh.", shop: true, tag: "Tile" },
  { name: "Removable Accent Panels", desc: "Shiplap or slat wall. Peel, press, done.", shop: true, tag: "Walls" },
  { name: "Interchangeable Cabinet Hardware", desc: "Swap pulls and knobs in under 30 min per unit.", shop: true, tag: "Hardware" }
];

// ─── Vendor Materials Catalog ───
const VENDOR_CATALOG = [
  {
    vendor: "Sherwin-Williams",
    logo: "SW",
    account: "Pro Account (Commercial Pricing)",
    accentColor: "#0055A5",
    products: [
      { name: "ProMar 200 Zero VOC Interior Latex", category: "Wall Paint", use: "Standard wall paint for all units — durable, low odor, fast dry", coverage: "350-400 sq ft/gallon", finish: "Eggshell", note: "2 coats recommended. Ask for commercial pricing." },
      { name: "ProMar 200 Interior Latex Semi-Gloss", category: "Trim & Door Paint", use: "All trim, doors, cabinets, and bathroom walls", coverage: "350-400 sq ft/gallon", finish: "Semi-Gloss", note: "Moisture/scrub resistant. Ideal for high-touch surfaces." },
      { name: "ProMar 400 Interior Latex Flat", category: "Ceiling Paint", use: "Ceiling-only flat finish for clean, even coverage", coverage: "400-450 sq ft/gallon", finish: "Flat", note: "Budget ceiling option. Hides imperfections." },
      { name: "Primer - Multi-Purpose Latex", category: "Primer", use: "Dark-to-light color changes, stain blocking, new drywall", coverage: "300-350 sq ft/gallon", finish: "Flat", note: "Required on all repairs, patches, and color changes." },
      { name: "Creamy (SW 7012)", category: "Wall Color", use: "Primary neutral — warm white that photographs well", coverage: "N/A", finish: "Eggshell", note: "Core palette color. Works in every unit type." },
      { name: "Accessible Beige (SW 7036)", category: "Wall Color", use: "Warm neutral — pairs with earth tone palettes", coverage: "N/A", finish: "Eggshell", note: "Deep Earth & Organic Khaki palette base." },
      { name: "Extra White (SW 7006)", category: "Trim Color", use: "Standard trim/door/ceiling white", coverage: "N/A", finish: "Semi-Gloss", note: "Universal trim color across all palettes." },
    ]
  },
  {
    vendor: "HD Supply",
    logo: "HD",
    account: "Facilities Maintenance Account",
    accentColor: "#F96302",
    products: [
      { name: "Matte Black Cabinet Bar Pulls (5\" center)", category: "Hardware", use: "Standard kitchen and bath cabinet pulls", coverage: "Per piece", finish: "Matte Black", note: "Bulk pricing at 50+ units. Matches Smoky Jade palette." },
      { name: "Brushed Nickel Cabinet Knobs", category: "Hardware", use: "Budget option for knob-style cabinets", coverage: "Per piece", finish: "Brushed Nickel", note: "Versatile — works with any neutral palette." },
      { name: "Single-Handle Bath Faucet (Chrome)", category: "Plumbing Fixtures", use: "Standard bathroom faucet replacement", coverage: "Per unit", finish: "Chrome", note: "ADA compliant. Easy single-hand operation." },
      { name: "LED Vanity Light Bar (24\")", category: "Light Fixtures", use: "Bathroom vanity lighting", coverage: "Per unit", finish: "Chrome/Matte Black", note: "LED — no bulb replacement. Energy efficient." },
      { name: "GFCI Outlet (15A, Self-Test)", category: "Electrical", use: "Required within 6 ft of water sources (NSPIRE)", coverage: "Per unit", finish: "White", note: "NSPIRE compliance item. Replace all non-GFCI in kitchens/baths." },
      { name: "Smoke Detector (10-Year Battery)", category: "Safety", use: "Every bedroom + hallway (NSPIRE requirement)", coverage: "Per unit", finish: "White", note: "Sealed battery — no maintenance for 10 years." },
      { name: "Door Lever Handle (Privacy)", category: "Hardware", use: "Bedroom and bathroom door levers", coverage: "Per door", finish: "Satin Nickel", note: "ADA lever style. Bulk pricing available." },
    ]
  },
  {
    vendor: "Ferguson",
    logo: "FG",
    account: "Contractor Account (Net Terms Available)",
    accentColor: "#003366",
    products: [
      { name: "Elongated Toilet (1.28 GPF WaterSense)", category: "Plumbing", use: "Standard toilet replacement — water efficient", coverage: "Per unit", finish: "White", note: "WaterSense certified. Meets all code requirements." },
      { name: "Kitchen Faucet (Single Handle, Pull-Down)", category: "Plumbing Fixtures", use: "Standard kitchen faucet — pull-down sprayer", coverage: "Per unit", finish: "Chrome/Matte Black", note: "Dual spray mode. Matches palette hardware finish." },
      { name: "P-Trap Assembly (1-1/2\" PVC)", category: "Plumbing Parts", use: "Under-sink drain repair", coverage: "Per sink", finish: "PVC White", note: "Keep 2-3 in truck stock. Common repair item." },
      { name: "Shower Valve Trim Kit", category: "Plumbing Fixtures", use: "Shower handle and trim upgrade", coverage: "Per shower", finish: "Chrome", note: "Pressure-balancing. Matches faucet finish." },
    ]
  },
  {
    vendor: "Home Depot Pro",
    logo: "PRO",
    account: "Pro Xtra Account (Volume Pricing)",
    accentColor: "#F96302",
    products: [
      { name: "TrafficMaster LVP (Peel & Stick)", category: "Flooring", use: "Budget vinyl plank — swift turn-key installs", coverage: "24 sq ft/box", finish: "Wood Look", note: "Peel and stick. No adhesive needed. 2-4 hour install." },
      { name: "LifeProof LVP (Click-Lock)", category: "Flooring", use: "Premium vinyl plank — waterproof, durable", coverage: "20 sq ft/box", finish: "Wood Look", note: "Click-lock install. Waterproof core. 10+ year warranty." },
      { name: "White Gloss Subway Tile (3x12)", category: "Tile", use: "Kitchen backsplash and bathroom surround", coverage: "Per sq ft", finish: "Gloss White", note: "Timeless. Works with every palette." },
      { name: "Patch & Repair Compound (1 Qt)", category: "Wall Repair", use: "Drywall holes, nail pops, screw holes", coverage: "Per container", finish: "White (sandable)", note: "Keep in truck stock. Sand smooth, then prime + paint." },
      { name: "Caulk - Kitchen & Bath (White, Silicone)", category: "Sealant", use: "All tub/shower/sink/countertop seams", coverage: "Per tube", finish: "White", note: "100% silicone. Mold resistant. Required on all wet area seams." },
      { name: "Painter's Tape (1.88\" x 60yd)", category: "Supplies", use: "Clean paint lines on trim, ceilings, accent walls", coverage: "Per roll", finish: "Blue", note: "14-day clean removal. Stock 3-4 rolls per unit." },
    ]
  },
  {
    vendor: "Amazon Sourced Materials",
    logo: "AM",
    account: "Amazon Business Account — Bulk Pricing",
    accentColor: "#FF9900",
    products: [
      { name: "Art3d 10-Sheet White Subway w/ Black Grout", category: "Peel & Stick Backsplash", use: "Classic kitchen backsplash — photographs clean in every palette", coverage: "~10 sq ft / pack", finish: "Gloss White", note: "$29.99/pack. Vinyl. Kitchen walls only — NOT within 12\" of stove burner." },
      { name: "Art3d 10-Pack Thickened Subway Tiles", category: "Peel & Stick Backsplash", use: "Higher-durability subway for long-cycle rentals", coverage: "~10 sq ft / pack", finish: "Gloss White", note: "$29.99/pack. 2-2.5mm thick. 3x stronger adhesive. Kitchen only." },
      { name: "Art3d 10-Pack Gray Marble Design", category: "Peel & Stick Backsplash", use: "Primary make-ready pick — best balance of cost, durability, neutral appeal", coverage: "~10 sq ft / pack", finish: "Gray Marble", note: "~$39.99/pack. $2.36/sq ft. Workforce + Class B/C apartments." },
      { name: "Art3d 10-Pack Beige Marble Stone", category: "Peel & Stick Backsplash", use: "Warm neutral alternative for earth-tone palettes", coverage: "~10 sq ft / pack", finish: "Beige Marble", note: "$39.99/pack. Pairs with Accessible Beige wall paint." },
      { name: "Art3d 100-Pc 3D Metal Subway (HEAT-SAFE)", category: "Peel & Stick Backsplash", use: "REQUIRED behind every stove/cooktop — heat-resistant to 250F+", coverage: "~12.5 sq ft", finish: "Brushed Metal 3D", note: "$59.99/pack. NSPIRE-compliant behind heat sources. NEVER substitute vinyl within 12\" of burners." },
      { name: "Art3d Stone Design Peel & Stick (10 sheets)", category: "Feature Wall Panels", use: "Accent wall in living room or bedroom feature wall", coverage: "~10 sq ft / pack", finish: "Stone Texture", note: "$25.64/pack. Works as feature wall in Design Studio palettes." },
      { name: "Art3d PVC 3D Wall Panels", category: "Feature Wall Panels", use: "Premium accent wall for Signature/Luxe staging", coverage: "Per panel", finish: "Multiple textures", note: "Use in photo-ready feature walls. Not for kitchen heat zones." },
      { name: "Art3d Glass Subway Tile (Peel & Stick)", category: "Peel & Stick Backsplash", use: "Premium glass look without traditional install time", coverage: "Per sheet", finish: "Gloss Glass", note: "Higher-end PM pitch. Verify heat zone before behind-stove use." },
      { name: "Crystal Chandelier Ceiling Fan (5-Blade, Black)", category: "Ceiling Fans & Lighting", use: "Signature / Luxe tier upgrade — primary bedroom, living room, dining", coverage: "Per unit", finish: "Matte Black + Crystal", note: "Amazon's Choice, 100+ bought/mo. Classic 5-blade w/ crystal bowl chandelier. $129-$189. Dimmable LED. Great for Class A Signature staging." },
      { name: "Bladeless Ring Ceiling Fan w/ App + Remote (White)", category: "Ceiling Fans & Lighting", use: "Luxe / Modern tier — smaller bedrooms, kids rooms, low-ceiling units", coverage: "Per unit", finish: "White Ring, Noiseless", note: "Smart-home compatible (WiFi app + remote). Noiseless 6-speed. $149-$219. Modern aesthetic, photographs clean on light-palette walls." },
      { name: "Crystal Ring Bladeless Fan w/ LED Halo (Black)", category: "Ceiling Fans & Lighting", use: "Luxe flagship — primary suites, living rooms, high-end units", coverage: "Per unit", finish: "Black Ring + Crystal LED", note: "1K+ bought/mo. App + remote controlled. Dimmable halo LED ring, 6-speed noiseless. $229-$319. Headliner for Luxe tier photos." },
      { name: "Rovibek 12-Pack Interlocking Deck Tiles (Pebble Gray)", category: "Outdoor / Waterproof Flooring", use: "Balcony, patio, outdoor hallway refresh — snap-together", coverage: "12 tiles = ~12 sq ft", finish: "Pebble Gray (also Muted Olive, Brown)", note: "$20-$25/pack. Waterproof, non-slip drainage base. Snaps over cracked concrete. Perfect for Class C balcony refresh without demo." },
      { name: "Rovibek 60-Pack Interlocking Deck Tiles (Pebble Gray)", category: "Outdoor / Waterproof Flooring", use: "Full patio or large balcony coverage (~60 sq ft)", coverage: "60 tiles = ~60 sq ft", finish: "Pebble Gray", note: "Bulk pack for full-patio jobs. ~$90-$110/pack. Install: under 2 hours by 1 crew member. No mortar, no demo, no mess." },
    ]
  },
  {
    vendor: "DFW Wholesale Suppliers (Trade-Only)",
    logo: "WS",
    account: "Cash Pro Accounts — Volume Savings Stay Internal",
    accentColor: "#0d9488",
    products: [
      { name: "Seconds & Surplus", category: "Wholesale Flooring & Beadboard", use: "Primary high-volume source for LVP flooring and beadboard wainscot materials", coverage: "Bulk pallets", finish: "Varies", note: "Best-in-DFW for LVP + beadboard combos. Irregular stock = huge savings vs. retail. Visit in person for best deals. Cash account protects credit." },
      { name: "Floor City", category: "Wholesale Flooring", use: "Specialized flooring distribution for bulk unit-turnover orders", coverage: "Pallet minimums", finish: "Commercial-grade LVP, LVT, VCT", note: "Trade-only distributor. Call ahead for bulk pricing on 10+ unit orders." },
      { name: "iDeal Floors", category: "Wholesale Flooring", use: "Competitive rates for unit turnovers; mid-range LVP + carpet tiles", coverage: "Bulk / pallet", finish: "Varies", note: "Reliable second-source for when Seconds & Surplus is low. Local DFW delivery." },
      { name: "ProSource Wholesale", category: "Multi-Category Trade Supplier", use: "Trade-only membership for various materials — flooring, paint, hardware, cabinets", coverage: "Full catalog", finish: "Commercial-grade across categories", note: "Membership required (free for licensed trades). Single source for diverse project needs. Good backup when single-vendor sourcing matters." },
      { name: "Legacy Builder Supply", category: "Multi-Category Wholesale", use: "Cash pro accounts for all building materials — protects personal/business credit", coverage: "Full catalog", finish: "Various", note: "Cash-only pro account model. Useful for protecting credit line during high-volume buying cycles. DFW-local stocked warehouse." },
      { name: "PurePly Flooring", category: "Commercial-Grade Flooring Distributor", use: "Premium commercial LVP for high-traffic rental units", coverage: "Pallet", finish: "Commercial-grade wear layer", note: "Best-in-class wear resistance for Class A/B properties. Higher cost but longer tenant-cycle lifespan." },
      { name: "Unico Flooring", category: "Commercial-Grade Flooring Distributor", use: "Alternative commercial-grade source; engineered wood + LVP", coverage: "Pallet", finish: "Commercial-grade", note: "Paired with PurePly as dual-source strategy for large portfolio contracts." },
      { name: "Relay Financial (Virtual Card Spend Tracking)", category: "Financial Operations Tool", use: "Issue virtual cards per-project to track all material spending by unit", coverage: "Per-unit card assignment", finish: "N/A", note: "Not a material supplier — operational tool. Each unit gets its own virtual card. Every swipe auto-logs to the unit's Line-Itemized Packet. Audit-proof spending trail." },
    ]
  },
];

const NSPIRE_CHECKLIST = [
  { id: "gfci", label: "GFCI protection within 6 ft of all water sources", category: "Electrical" },
  { id: "smoke", label: "Working smoke detectors in every bedroom + hallway", category: "Safety" },
  { id: "co", label: "CO detectors on every level with fuel-burning equipment", category: "Safety" },
  { id: "lighting", label: "Minimum lighting fixtures in kitchen, bath, and habitable rooms", category: "Electrical" },
  { id: "wiring", label: "No exposed wiring or junction boxes", category: "Electrical" },
  { id: "hvac", label: "HVAC system functional and adequate for unit size", category: "HVAC" },
  { id: "plumbing", label: "All plumbing functional, no active leaks", category: "Plumbing" },
  { id: "ventilation", label: "Bathroom and kitchen exhaust / ventilation operational", category: "Plumbing" },
  { id: "doors", label: "Fire-labeled doors functional, self-closing, no gaps", category: "Safety" },
  { id: "guardrails", label: "Guardrails secure on stairs and balconies", category: "Safety" },
  { id: "windows", label: "All windows operable, locks functional, no broken panes", category: "Structure" },
  { id: "pest", label: "No evidence of pest infestation", category: "Health" },
  { id: "mold", label: "No visible mold or water damage", category: "Health" },
  { id: "paint", label: "No peeling or chipping paint (lead-safe for pre-1978)", category: "Health" },
  { id: "backsplash_heat", label: "Backsplash behind stove is heat-safe (metal/ceramic only — no vinyl peel-and-stick within 12\" of burners)", category: "Kitchen" },
  { id: "backsplash_edges", label: "All peel-and-stick backsplash edges are sealed, no lifting or gaps", category: "Kitchen" }
];

// Coverage rates for material quantity calculations
const MATERIAL_SPECS = [
  { key: "wallPaint", label: "Wall Paint", unit: "gallon", coveragePerUnit: 350, coverageLabel: "sq ft / gallon (1 coat)", coats: 2, note: "2 coats recommended" },
  { key: "accentPaint", label: "Accent Wall Paint", unit: "gallon", coveragePerUnit: 350, coverageLabel: "sq ft / gallon (1 coat)", coats: 2, note: "2 coats recommended" },
  { key: "flooring", label: "Flooring (LVP/VGT)", unit: "box", coveragePerUnit: 24, coverageLabel: "sq ft / box", coats: 1, note: "+10% waste factor applied" },
  { key: "tile", label: "Tile & Backsplash", unit: "sq ft", coveragePerUnit: 1, coverageLabel: "sq ft", coats: 1, note: "+10% waste factor applied" },
  { key: "vanity", label: "Vanity / Cabinets", unit: "unit", coveragePerUnit: 1, coverageLabel: "per unit", coats: 1, note: "Per bathroom" },
  { key: "countertop", label: "Countertop", unit: "linear ft", coveragePerUnit: 1, coverageLabel: "linear ft", coats: 1, note: "Measured at site" },
  { key: "hardware", label: "Hardware & Pulls", unit: "piece", coveragePerUnit: 1, coverageLabel: "per piece", coats: 1, note: "Pulls, knobs, handles" },
  { key: "fixtures", label: "Light Fixtures", unit: "unit", coveragePerUnit: 1, coverageLabel: "per fixture", coats: 1, note: "Sconces, vanity lights, pendants" }
];

const ShopBadge = () => (
  <span className="ds-shop-badge" data-testid="shop-badge">Shop MF Store</span>
);



// ─── Doctrine v3.0 Strip (Feb 11, 2026) ───
// Codifies the DESIGN_STUDIO_MASTER_INTEGRATION_ENGINE JSON spec into a
// visible header strip so Shannon + Shaneka always see the doctrine
// while working — pricing model, turn tracks, anti-cookie-cutter mandate,
// AI render disclosure.
const DoctrineStripV3 = ({ monthlyRent = 0 }) => {
  const [turnTrack, setTurnTrack] = useState("tier_2_standard");
  const [stab, setStab] = useState(null);
  const rent = Number(monthlyRent) || 0;
  const upliftAmt = Math.round(rent * 0.20 * 100) / 100;
  const stabilized = Math.round((rent + upliftAmt) * 100) / 100;
  const annual = Math.round(upliftAmt * 12 * 100) / 100;

  const tracks = [
    { k: "tier_1_rapid",    code: "T1", label: "48-Hour Rapid",  color: "#F87171" },
    { k: "tier_2_standard", code: "T2", label: "14-Day Standard",color: "#FFB347" },
    { k: "tier_3_full",     code: "T3", label: "21-Day Full Dignity", color: "#00DC78" },
  ];

  return (
    <div data-testid="doctrine-v3-strip" style={{
      background: "linear-gradient(135deg, rgba(232,184,196,0.10), rgba(184,145,90,0.08))",
      border: "1px solid rgba(232,184,196,0.35)",
      borderRadius: 8,
      padding: "0.9rem 1.1rem",
      marginBottom: "1rem",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.8rem" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: "#E8B8C4", fontSize: "0.66rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
            Design Studio · Doctrine v3.0
          </div>
          <div style={{ color: "#F3F4F6", fontSize: "0.92rem", fontWeight: 700, marginTop: 3 }}>
            Universal Unit Dignity · Anti-Cookie-Cutter · Zero Demolition
          </div>
          <div style={{ color: "#ADBED1", fontSize: "0.76rem", marginTop: 2, lineHeight: 1.5 }}>
            Renders below are <strong>Design Concepts — not photos of the actual unit</strong>. They visualize your variation + material choices so owners can see the finish. Real after-photos replace them in the Memory Book once the crew completes the turn.
          </div>
        </div>
      </div>

      {/* Row 2: Stabilization ROI + Turn Track selector */}
      <div style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(240px, 1.2fr) 1fr", gap: "0.7rem" }}>
        <div style={{ background: "rgba(0,220,120,0.08)", border: "1px solid rgba(0,220,120,0.35)", borderRadius: 6, padding: "0.55rem 0.75rem" }}>
          <div style={{ color: "#00DC78", fontSize: "0.66rem", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 800 }}>
            Stabilization ROI · Market Rent +20%
          </div>
          {rent > 0 ? (
            <div style={{ color: "#F5EFE1", fontSize: "0.86rem", marginTop: 3, fontWeight: 600 }}>
              ${rent.toFixed(0)}  →  <strong style={{ color: "#00DC78" }}>${stabilized.toFixed(0)}/mo</strong>
              <span style={{ color: "#ADBED1", fontSize: "0.74rem", fontWeight: 500 }}>  ·  +${upliftAmt.toFixed(0)}/mo  ·  +${annual.toFixed(0)}/yr uplift</span>
            </div>
          ) : (
            <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginTop: 3, fontStyle: "italic" }}>Enter monthly rent below to see the ROI signal.</div>
          )}
        </div>
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, padding: "0.55rem 0.75rem" }}>
          <div style={{ color: "#B8915A", fontSize: "0.66rem", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 800 }}>
            Turn Track
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 5, flexWrap: "wrap" }}>
            {tracks.map((t) => (
              <button
                key={t.k}
                type="button"
                data-testid={`doctrine-track-${t.k}`}
                onClick={() => setTurnTrack(t.k)}
                style={{ background: turnTrack === t.k ? `${t.color}22` : "transparent",
                         border: `1px solid ${turnTrack === t.k ? t.color : "rgba(255,255,255,0.14)"}`,
                         color: turnTrack === t.k ? t.color : "#ADBED1",
                         padding: "3px 8px", borderRadius: 12, fontSize: "0.72rem",
                         fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                {t.code} · {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Design Studio ───
export default function DesignStudio() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authedUser, setAuthedUser] = useState(null);
  const [propertyAddress, setPropertyAddress] = useState("");
  const [linkedProjectId, setLinkedProjectId] = useState("");
  const [unitType, setUnitType] = useState("2bed");
  const [nspireChecks, setNspireChecks] = useState({});
  const [activeTab, setActiveTab] = useState("palettes");

  // Feb 12, 2026 — support deep-link from Cockpit's "NSPIRE Walkthrough"
  // button (e.g. /design-studio?tab=nspire). Uses existing tab state.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      if (t && ["palettes", "solutions", "specs", "swift", "nspire", "budget", "materials"].includes(t)) {
        setActiveTab(t);
      }
    } catch (_) { /* ignore */ }
  }, []);
  // Design Solutions library (P1) + per-slot photo editor (opens the shared modal)
  const [solutions, setSolutions] = useState([]);
  const [solutionsLoading, setSolutionsLoading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [budgetItems, setBudgetItems] = useState({});
  const [specs, setSpecs] = useState({
    bedrooms: 2, bathrooms: 2, kitchen_sqft: 120, living_sqft: 350,
    bedroom_sqft: 150, bathroom_sqft: 60, cabinet_doors: 12,
    drawer_count: 6, light_fixtures: 6, sinks: 3, ceiling_height: 9
  });
  const [generatingPitch, setGeneratingPitch] = useState(false);
  const [generatingSignoff, setGeneratingSignoff] = useState(false);
  const [emailingSignoff, setEmailingSignoff] = useState(false);
  const [emailStatus, setEmailStatus] = useState("");
  const [monthlyRent, setMonthlyRent] = useState(2000);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [portfolioDiscount, setPortfolioDiscount] = useState(false);

  // PURGED (Feb 12, 2026): activePalette, selections, lockedVisual, summaryRef,
  // generating, and the vizProspect/schedule-later state block were removed with
  // the Mix & Match / Summary / Public Room Visualizer / Send-to-Prospect
  // sections. Dynamic Materials Builder owns finish selection end-to-end.

  // Memory Book gallery state
  const [mbEntries, setMbEntries] = useState([]);
  const [mbLoading, setMbLoading] = useState(false);
  const [mbDetail, setMbDetail] = useState(null);
  const [mbPdfBusy, setMbPdfBusy] = useState(false);

  // Landlord Proposal (calculator + email) state
  const [mbCalcUnitType, setMbCalcUnitType] = useState("2bed");
  const [mbCalcCycle, setMbCalcCycle] = useState("standard_14d");
  // Boom 1+2+3: sqft-driven single-room refresh
  const [mbRoomSqft, setMbRoomSqft] = useState("");
  const [mbScopeTier, setMbScopeTier] = useState("full_refresh");
  const [mbEstimating, setMbEstimating] = useState(false);
  const [mbEstimateInfo, setMbEstimateInfo] = useState(null);
  const [mbLinkedRooms, setMbLinkedRooms] = useState([]);
  const [mbWallTreatments, setMbWallTreatments] = useState([]);
  const [mbCalc, setMbCalc] = useState(null);
  const [mbCalcBusy, setMbCalcBusy] = useState(false);
  const [mbEmailOpen, setMbEmailOpen] = useState(false);
  const [mbLandlordName, setMbLandlordName] = useState("");
  const [mbLandlordEmail, setMbLandlordEmail] = useState("");
  const [mbPropertyAddress, setMbPropertyAddress] = useState("");
  const [mbCustomMessage, setMbCustomMessage] = useState("");
  const [mbEmailBusy, setMbEmailBusy] = useState(false);
  const [mbEmailStatus, setMbEmailStatus] = useState("");
  const [mbProposalBusy, setMbProposalBusy] = useState(false);
  const [mbTrecFindings, setMbTrecFindings] = useState([]);

  const loadMemoryBook = async () => {
    setMbLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/design/memory-book`, { credentials: "include" });
      const data = await r.json();
      if (r.ok) setMbEntries(data.entries || []);
    } catch (e) { /* noop */ }
    setMbLoading(false);
  };

  useEffect(() => {
    if (activeTab === "memory" && authChecked) {
      loadMemoryBook();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, authChecked]);

  // Design Solutions loader (P1) — pulls MF's curated Design Solutions library
  useEffect(() => {
    if (activeTab !== "solutions" || !authChecked) return;
    if (solutions.length > 0) return;
    (async () => {
      setSolutionsLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/design-solutions`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          setSolutions(d.solutions || []);
        }
      } catch { /* silent */ }
      setSolutionsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, authChecked]);

  const downloadMemoryBookPdf = async (entry_id, title) => {
    setMbPdfBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/design/memory-book/${entry_id}/pdf`, { credentials: "include" });
      if (!r.ok) throw new Error("PDF generation failed");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MF-MemoryBook-${(title || "entry").replace(/[^a-z0-9-_]/gi, "_").slice(0, 60)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Memory Book PDF failed:", e);
    }
    setMbPdfBusy(false);
  };

  const deleteMemoryBookEntry = async (entry_id) => {
    if (!window.confirm("Delete this Memory Book entry? This cannot be undone.")) return;
    try {
      await fetch(`${BACKEND_URL}/api/design/memory-book/${entry_id}`, { method: "DELETE", credentials: "include" });
      setMbDetail(null);
      loadMemoryBook();
    } catch (e) { /* noop */ }
  };

  // Reset proposal/email state when modal opens for a different entry
  useEffect(() => {
    if (mbDetail) {
      setMbCalc(null);
      setMbTrecFindings([]);
      setMbEmailOpen(false);
      setMbEmailStatus("");
      setMbCustomMessage("");
      setMbRoomSqft("");
      setMbEstimateInfo(null);
      setMbScopeTier("full_refresh");
      setMbWallTreatments([]);
      setMbLinkedRooms([]);
      // Auto-pull linked-project rooms if this entry is tied to a project
      (async () => {
        try {
          const r = await fetch(`${BACKEND_URL}/api/design/memory-book/${mbDetail.entry_id}/linked-project-rooms`, { credentials: "include" });
          const data = await r.json();
          if (r.ok && data.linked && Array.isArray(data.rooms) && data.rooms.length > 0) {
            setMbLinkedRooms(data.rooms);
          }
        } catch (e) { /* noop */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mbDetail?.entry_id]);

  const estimateRoomSize = async () => {
    if (!mbDetail) return;
    const photoB64 = mbDetail.before_image_b64 || mbDetail.after_image_b64;
    if (!photoB64) {
      setMbEstimateInfo({ ok: false, error: "No photo attached to this entry to estimate from." });
      return;
    }
    setMbEstimating(true);
    setMbEstimateInfo(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/design/estimate-room-size`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: photoB64 }),
      });
      const data = await r.json();
      if (r.ok && data.ok) {
        setMbRoomSqft(String(data.estimated_sqft));
        setMbEstimateInfo(data);
        setMbCalc(null);
      } else {
        setMbEstimateInfo({ ok: false, error: data.error || data.detail || "Vision estimate failed." });
      }
    } catch (e) {
      setMbEstimateInfo({ ok: false, error: "Network error during estimate." });
    }
    setMbEstimating(false);
  };

  const runMemoryBookCalculator = async () => {
    if (!mbDetail) return;
    setMbCalcBusy(true);
    setMbCalc(null);
    try {
      const sqftNum = parseFloat(mbRoomSqft) || 0;
      const r = await fetch(`${BACKEND_URL}/api/design/memory-book/${mbDetail.entry_id}/calculator`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unit_type: mbCalcUnitType,
          cycle: mbCalcCycle,
          room_sqft: sqftNum,
          scope_tier: mbScopeTier,
        }),
      });
      const data = await r.json();
      if (r.ok && data.ok) setMbCalc(data.calc);
    } catch (e) { /* noop */ }
    setMbCalcBusy(false);
  };

  // P2 — Auto-fire the calculator whenever inputs change. 400ms debounce
  // so we don't hammer the endpoint while Shannon types in the sqft field.
  useEffect(() => {
    if (!mbDetail?.entry_id) return;
    const t = setTimeout(() => {
      runMemoryBookCalculator();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mbDetail?.entry_id, mbCalcUnitType, mbCalcCycle, mbRoomSqft, mbScopeTier]);

  const downloadProposalPdf = async () => {
    if (!mbDetail) return;
    setMbProposalBusy(true);
    setMbTrecFindings([]);
    try {
      const sqftNum = parseFloat(mbRoomSqft) || 0;
      const wallNotes = mbWallTreatments.length > 0
        ? ("\n\nReversible wall treatments: " + mbWallTreatments.join(", ") + ". No paint. No demo.")
        : "";
      const r = await fetch(`${BACKEND_URL}/api/design/memory-book/${mbDetail.entry_id}/proposal-pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unit_type: mbCalcUnitType,
          cycle: mbCalcCycle,
          room_sqft: sqftNum,
          scope_tier: mbScopeTier,
          landlord_name: mbLandlordName,
          property_address: mbPropertyAddress,
          custom_message: (mbCustomMessage || "") + wallNotes,
        }),
      });
      const ct = r.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const data = await r.json();
        if (data.trec_violations) setMbTrecFindings(data.findings || []);
      } else {
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `MF-FlatFee-Proposal-${(mbDetail.title || "entry").replace(/[^a-z0-9-_]/gi, "_").slice(0, 60)}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) { /* noop */ }
    setMbProposalBusy(false);
  };

  const emailProposalToLandlord = async () => {
    if (!mbDetail) return;
    if (!mbLandlordEmail || !mbLandlordEmail.includes("@")) {
      setMbEmailStatus("Enter a valid landlord email first.");
      return;
    }
    setMbEmailBusy(true);
    setMbEmailStatus("");
    setMbTrecFindings([]);
    try {
      const sqftNum = parseFloat(mbRoomSqft) || 0;
      const wallNotes = mbWallTreatments.length > 0
        ? ("\n\nReversible wall treatments: " + mbWallTreatments.join(", ") + ". No paint. No demo.")
        : "";
      const r = await fetch(`${BACKEND_URL}/api/design/memory-book/${mbDetail.entry_id}/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landlord_name: mbLandlordName,
          landlord_email: mbLandlordEmail.trim().toLowerCase(),
          property_address: mbPropertyAddress,
          custom_message: (mbCustomMessage || "") + wallNotes,
          unit_type: mbCalcUnitType,
          cycle: mbCalcCycle,
          room_sqft: sqftNum,
          scope_tier: mbScopeTier,
        }),
      });
      const data = await r.json();
      if (r.ok && data.ok) {
        setMbEmailStatus(`✓ Sent to ${mbLandlordEmail} with the locked $${Number(data.flat_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} flat fee.`);
        setTimeout(() => { setMbEmailOpen(false); setMbEmailStatus(""); }, 4000);
      } else if (data.trec_violations) {
        setMbTrecFindings(data.findings || []);
        setMbEmailStatus("Blocked by TREC firewall — see violations below.");
      } else {
        setMbEmailStatus(data.detail || "Send failed. Try again.");
      }
    } catch (e) {
      setMbEmailStatus("Network error. Try again.");
    }
    setMbEmailBusy(false);
  };

  // Admin auth guard — Design Studio is internal-only
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        const u = data?.user || data;
        const role = u?.dashboard_role || u?.role || "";
        const allowed = role === "ceo" || role === "coo" || role === "admin";
        if (!u) {
          setAuthError("not_authed");
          setAuthChecked(true);
          return;
        }
        if (!allowed) {
          setAuthError("not_admin");
          setAuthChecked(true);
          return;
        }
        setAuthedUser(u);
        setAuthChecked(true);
      })
      .catch(() => {
        if (cancelled) return;
        setAuthError("network");
        setAuthChecked(true);
      });
    return () => { cancelled = true; };
  }, [navigate]);


  // Update a spec field
  const updateSpec = (key, value) => {
    setSpecs(prev => ({ ...prev, [key]: Number(value) || 0 }));
  };

  // Auto-populate budget from specs
  const autoPopulateBudget = () => {
    const s = specs;
    const totalFloor = s.kitchen_sqft + s.living_sqft + (s.bedroom_sqft * s.bedrooms) + (s.bathroom_sqft * s.bathrooms);
    const totalWall = totalFloor * 0.4 * s.ceiling_height * 2;
    const tileSqft = Math.round(s.bathroom_sqft * s.bathrooms * 0.6);

    setBudgetItems(prev => ({
      ...prev,
      wallPaint: { ...prev.wallPaint, sqft: Math.round(totalWall) },
      accentPaint: { ...prev.accentPaint, sqft: Math.round(s.living_sqft * 0.3) },
      flooring: { ...prev.flooring, sqft: Math.round(totalFloor) },
      tile: { ...prev.tile, sqft: tileSqft },
      vanity: { ...prev.vanity, sqft: s.bathrooms },
      countertop: { ...prev.countertop, sqft: Math.round(s.kitchen_sqft * 0.15) },
      hardware: { ...prev.hardware, sqft: s.cabinet_doors + s.drawer_count },
      fixtures: { ...prev.fixtures, sqft: s.light_fixtures }
    }));
    setActiveTab("budget");
  };

  // Generate Pitch PDF
  const handleGeneratePitch = async () => {
    setGeneratingPitch(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/design/generate-pitch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_address: propertyAddress || "DFW Metroplex",
          unit_type: unitType,
          specs: specs
        })
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MF-Pitch-Proposal.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Pitch PDF failed:", err); }
    setGeneratingPitch(false);
  };

  // Generate Sign-Off PDF (Scope-Lock Agreement)
  const handleGenerateSignoff = async () => {
    setGeneratingSignoff(true);
    try {
      // Build materials list from current budgetItems
      const materials_list = Object.entries(budgetItems)
        .filter(([k, v]) => v && v.sqft && v.sqft > 0)
        .map(([k, v]) => {
          const spec = MATERIAL_SPECS.find(s => s.key === k) || {};
          const qty = calcQty(spec, v.sqft);
          const unitCost = Number(v.cost || 0);
          return {
            name: spec.label || k,
            quantity: qty,
            unit: spec.unit || "",
            unit_cost: unitCost,
            line_total: qty * unitCost
          };
        });

      const response = await fetch(`${BACKEND_URL}/api/design/generate-signoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_address: propertyAddress || "DFW Metroplex",
          unit_type: unitType,
          client_name: clientName,
          monthly_rent: Number(monthlyRent) || 0,
          markup_pct: portfolioDiscount ? 0.15 : 0.20,
          palette_name: "",
          palette_description: "",
          materials_list,
          before_image_b64: "",
          after_image_b64: "",
          room_type: "",
          project_id: linkedProjectId || ""
        })
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MF-Scope-Lock-Agreement.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Sign-off PDF failed:", err); }
    setGeneratingSignoff(false);
  };

  // Email Scope-Lock Agreement to Client (via Resend)
  const handleEmailSignoff = async () => {
    if (!clientEmail || !clientEmail.includes("@")) {
      setEmailStatus("Enter a valid client email first.");
      setTimeout(() => setEmailStatus(""), 4000);
      return;
    }
    setEmailingSignoff(true);
    setEmailStatus("");
    try {
      const materials_list = Object.entries(budgetItems)
        .filter(([k, v]) => v && v.sqft && v.sqft > 0)
        .map(([k, v]) => {
          const spec = MATERIAL_SPECS.find(s => s.key === k) || {};
          const qty = calcQty(spec, v.sqft);
          const unitCost = Number(v.cost || 0);
          return {
            name: spec.label || k,
            quantity: qty,
            unit: spec.unit || "",
            unit_cost: unitCost,
            line_total: qty * unitCost
          };
        });
      const response = await fetch(`${BACKEND_URL}/api/design/email-signoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_address: propertyAddress || "DFW Metroplex",
          unit_type: unitType,
          client_name: clientName,
          client_email: clientEmail,
          monthly_rent: Number(monthlyRent) || 0,
          markup_pct: portfolioDiscount ? 0.15 : 0.20,
          palette_name: "",
          palette_description: "",
          materials_list,
          before_image_b64: "",
          after_image_b64: "",
          room_type: "",
          project_id: linkedProjectId || ""
        })
      });
      if (response.ok) {
        setEmailStatus(`Sent to ${clientEmail}`);
      } else {
        const err = await response.json().catch(() => ({}));
        setEmailStatus(err.detail || "Email failed. Try again.");
      }
    } catch (err) {
      console.error("Email signoff failed:", err);
      setEmailStatus("Email failed. Check connection.");
    }
    setEmailingSignoff(false);
    setTimeout(() => setEmailStatus(""), 6000);
  };

  // Budget helpers
  const updateBudget = (key, field, value) => {
    setBudgetItems(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  const calcQty = (spec, sqft) => {
    if (!sqft || sqft <= 0) return 0;
    const waste = (spec.key === "flooring" || spec.key === "tile") ? 1.1 : 1;
    const coats = spec.coats || 1;
    const raw = (sqft * waste * coats) / spec.coveragePerUnit;
    return Math.ceil(raw);
  };

  const getBudgetTotal = () => {
    let total = 0;
    MATERIAL_SPECS.forEach(spec => {
      const item = budgetItems[spec.key];
      if (item?.sqft && item?.costPerUnit) {
        total += calcQty(spec, item.sqft) * item.costPerUnit;
      }
    });
    return total;
  };

  // Per-unit budget caps (Shannon's internal operating targets — materials only, excludes stabilization fee)
  const UNIT_BUDGET_CAPS = {
    studio: 1800,
    "1bed":  2200,
    "2bed":  2800,
    "3bed":  3600,
    "4bed":  4500,
  };
  const currentCap = UNIT_BUDGET_CAPS[unitType] || 2800;
  const budgetUsed = getBudgetTotal();
  const budgetPct = currentCap ? Math.min(100, Math.round((budgetUsed / currentCap) * 100)) : 0;
  const budgetOver = budgetUsed > currentCap;

  // PURGED (Feb 12, 2026): applyFullPalette, selectMaterial, and getSelected
  // helpers along with the `selections`/`activePalette` state removed with
  // the Mix & Match + Summary tabs. Dynamic Materials Builder is now the
  // single source of truth for procurement selection.

  // Toggle NSPIRE item
  const toggleNspire = (id) => {
    setNspireChecks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const nspireCount = Object.values(nspireChecks).filter(Boolean).length;

  // PURGED (Feb 12, 2026): handleGeneratePDF used the legacy PALETTES-driven
  // Summary tab. Replaced by handleGeneratePitch + handleGenerateSignoff
  // which now pull directly from Dynamic Materials Builder + Budget tab.

  return (
    <div className="ds-page" data-testid="design-studio">
      <PanicBackButton />
      {!authChecked ? (
        <div className="ds-auth-loading" data-testid="ds-auth-loading" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "2rem", color: "#ADBED1", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Verifying admin access&hellip;</div>
          <div style={{ fontSize: 12, color: "#7E92A8", marginTop: 8 }}>Design Studio is internal-only. Clients receive visualizations via direct email.</div>
          <button onClick={() => navigate("/dashboard")} style={{ marginTop: 24, background: "transparent", border: "1px solid rgba(167,139,250,0.4)", color: "#A78BFA", padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>← Back to Dashboard</button>
        </div>
      ) : authError ? (
        <div data-testid="ds-auth-error" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "2rem", color: "#ADBED1", textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎨</div>
          <h2 style={{ color: "#E6ECF2", fontSize: 20, margin: "0 0 8px", fontWeight: 800 }}>
            {authError === "not_authed" ? "Sign in to use the Design Studio" : authError === "not_admin" ? "Design Studio is admin-only" : "Couldn't verify your access"}
          </h2>
          <p style={{ fontSize: 13.5, lineHeight: 1.55, margin: "0 0 18px", color: "#ADBED1" }}>
            {authError === "not_authed"
              ? "The Design Studio is internal. Sign in with your Moovin Forward team account to render rooms and build proposals."
              : authError === "not_admin"
              ? "Your account exists but doesn't have admin (CEO/COO) access. Ask the owner to grant it, or head back to your client portal."
              : "Network hiccup or session expired. Refresh, sign back in, or head back to your dashboard."}
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            <button onClick={() => navigate("/dashboard")} style={{ background: "linear-gradient(135deg, #A78BFA 0%, #00C8C8 100%)", border: "none", color: "#1a1a2e", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer" }} data-testid="ds-auth-back-dashboard">← Back to Dashboard</button>
            {authError === "not_authed" && (
              <button onClick={() => navigate("/login")} style={{ background: "transparent", border: "1px solid rgba(0,200,200,0.5)", color: "#00C8C8", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }} data-testid="ds-auth-sign-in">Sign In</button>
            )}
            <button onClick={() => navigate("/")} style={{ background: "transparent", border: "1px solid rgba(173,190,209,0.3)", color: "#ADBED1", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Home</button>
          </div>
        </div>
      ) : (
      <>
      <nav className="ds-nav">
        <div className="ds-nav-inner">
          <Link to="/" className="ds-nav-logo">
            <img src={LOGO_URL} alt="Moovin Forward" className="ds-logo-img" />
          </Link>
          <h1 className="ds-nav-title">Design Studio</h1>
          <div className="ds-nav-right">
            <Link to="/dashboard" className="ds-nav-link" data-testid="ds-back-dashboard">Dashboard</Link>
            <Link to="/" className="ds-nav-link">Home</Link>
          </div>
        </div>
      </nav>

      <div className="ds-body">
        {/* Doctrine v3.0 strip — Feb 11, 2026. Codified pricing, turn tracks, anti-cookie-cutter mandate.
            Renders as an inline strip so Shannon and Shaneka see the doctrine every time they open the studio. */}
        <DoctrineStripV3 monthlyRent={monthlyRent} />

        {/* Property Header */}
        <div className="ds-property-header" data-testid="ds-property-header">
          <div className="ds-property-inputs">
            <div className="ds-input-group">
              <label>Address</label>
              <input
                type="text"
                placeholder="Unit address or complex name"
                value={propertyAddress}
                onChange={e => setPropertyAddress(e.target.value)}
                data-testid="ds-property-address"
              />
            </div>
            <div className="ds-input-group ds-input-sm">
              <label>Unit Type</label>
              <select value={unitType} onChange={e => setUnitType(e.target.value)} data-testid="ds-unit-type">
                <option value="studio">Studio</option>
                <option value="1bed">1 Bed / 1 Bath</option>
                <option value="2bed">2 Bed / 2 Bath</option>
                <option value="3bed">3 Bed / 2+ Bath</option>
              </select>
            </div>
          </div>
        </div>

        {/* Test Run Sentinel — Shannon's dogfood banner. Watches the
            address input and lights up when a sentinel address is typed. */}
        <TestRunBanner address={propertyAddress} />

        {/* System Doctrine — unified hardwires (Feb 2, 2026 continuation
            merge). Renders inline (no new route, no new page). Surfaces
            OSHA 30, 48h turn readiness, NSPIRE/City/Section 8/HUD-VASH
            + accessibility hardwires, the acclimation + jobsite security
            clause, the 50/50 pricing model, and the 4 architectural
            unit variations with Active Layout Render status. */}
        <SystemDoctrine />

        {/* Tabs — grouped two-tier navigation */}
        <div className="ds-tab-groups" data-testid="ds-tab-groups" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          {[
            { id: "library",    label: "🎨 Design Library",    tabs: ["palettes", "solutions"] },
            { id: "compliance", label: "🛡️ Compliance & Specs", tabs: ["specs", "swift", "nspire"] },
            { id: "pricing",    label: "💵 Pricing",           tabs: ["budget", "materials"] },
            { id: "field",      label: "🛠 Field Mode",        tabs: ["field-inline"] },
            { id: "memory",     label: "🔖 Memory Book",       tabs: ["memory"] },
          ].map(g => {
            const isActive = g.tabs.includes(activeTab);
            return (
              <button
                key={g.id}
                type="button"
                data-testid={`ds-group-${g.id}`}
                onClick={() => setActiveTab(g.tabs[0])}
                style={{
                  background: isActive ? "linear-gradient(90deg, rgba(167,139,250,0.20), rgba(90,200,216,0.20))" : "rgba(15,20,30,0.5)",
                  border: `1px solid ${isActive ? "#A78BFA" : "rgba(255,255,255,0.08)"}`,
                  color: isActive ? "#F3F4F6" : "#ADBED1",
                  padding: "0.5rem 0.95rem",
                  borderRadius: 8,
                  fontSize: "0.82rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  letterSpacing: "0.02em",
                }}
              >{g.label}</button>
            );
          })}
        </div>

        <div className="ds-tabs" data-testid="ds-tabs">
          {["palettes","solutions"].includes(activeTab) && (
            <>
              <button className={`ds-tab ${activeTab === "palettes" ? "ds-tab-active" : ""}`} onClick={() => setActiveTab("palettes")} data-testid="ds-tab-palettes">Dynamic Materials</button>
              <button className={`ds-tab ${activeTab === "solutions" ? "ds-tab-active" : ""}`} onClick={() => setActiveTab("solutions")} data-testid="ds-tab-solutions">Design Solutions</button>
            </>
          )}
          {["specs","swift","nspire"].includes(activeTab) && (
            <>
              <button className={`ds-tab ${activeTab === "specs" ? "ds-tab-active" : ""}`} onClick={() => setActiveTab("specs")} data-testid="ds-tab-specs">Property Specs</button>
              <button className={`ds-tab ${activeTab === "swift" ? "ds-tab-active" : ""}`} onClick={() => setActiveTab("swift")} data-testid="ds-tab-swift">Swift Turn-Key</button>
              <button className={`ds-tab ${activeTab === "nspire" ? "ds-tab-active" : ""}`} onClick={() => setActiveTab("nspire")} data-testid="ds-tab-nspire">NSPIRE</button>
            </>
          )}
          {["budget","materials"].includes(activeTab) && (
            <>
              <button className={`ds-tab ${activeTab === "budget" ? "ds-tab-active" : ""}`} onClick={() => setActiveTab("budget")} data-testid="ds-tab-budget">
                Budget {getBudgetTotal() > 0 && <span className="ds-tab-count-red">${getBudgetTotal().toLocaleString()}</span>}
              </button>
              <button className={`ds-tab ${activeTab === "materials" ? "ds-tab-active" : ""}`} onClick={() => setActiveTab("materials")} data-testid="ds-tab-materials">Materials</button>
            </>
          )}
          {activeTab === "memory" && (
            <button className="ds-tab ds-tab-active" data-testid="ds-tab-memory">
              <i className="fas fa-bookmark" style={{ marginRight: 5, color: "#E8B8C4" }}></i>Memory Book
            </button>
          )}
          {activeTab === "field-inline" && (
            <button className="ds-tab ds-tab-active" data-testid="ds-tab-field-inline">
              🛠 Field Mode · Embedded
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="ds-content">
          {/* DYNAMIC MATERIALS TAB */}
          {activeTab === "palettes" && (
            <div className="ds-palettes-view ds-dual-panel" data-testid="ds-palettes-view">
              <p className="ds-section-desc">Every unit is unique. Enter your actual materials (brand, SKU, vendor, unit cost) below — the platform builds the procurement manifest and locks scope. No preset variations, no locked hardware.</p>
              <div className="ds-scope-lock-panel" data-testid="ds-scope-lock-panel" style={{ background: "rgba(0,200,200,0.06)", border: "1px solid rgba(0,200,200,0.3)", borderRadius: 8, padding: "14px 16px", marginBottom: 14 }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div className="ds-input-group ds-input-sm" style={{ flex: "1 1 180px" }}>
                    <label>Client / Representative Name</label>
                    <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Full name" data-testid="ds-client-name" />
                  </div>
                  <div className="ds-input-group ds-input-sm" style={{ flex: "1 1 200px" }}>
                    <label>Client Email</label>
                    <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="client@example.com" data-testid="ds-client-email" />
                  </div>
                  <div className="ds-input-group ds-input-sm" style={{ flex: "0 0 160px" }}>
                    <label>Unit Monthly Rent ($)</label>
                    <input type="number" min="0" value={monthlyRent} onChange={e => setMonthlyRent(e.target.value)} placeholder="2000" data-testid="ds-monthly-rent" />
                  </div>
                  <div className="ds-input-group ds-input-sm" style={{ flex: "0 0 auto" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input type="checkbox" checked={portfolioDiscount} onChange={e => setPortfolioDiscount(e.target.checked)} data-testid="ds-portfolio-discount" style={{ margin: 0 }} />
                      <span style={{ fontSize: 12 }}>Portfolio Discount (10+ units, 15%)</span>
                    </label>
                  </div>
                  <div className="ds-input-group ds-input-sm" style={{ flex: "0 0 200px" }}>
                    <label>Link to Project ID (optional — auto-archives PDF to project Documents)</label>
                    <input type="text" value={linkedProjectId} onChange={e => setLinkedProjectId(e.target.value)} placeholder="e.g. proj_abc123" data-testid="ds-linked-project-id" />
                  </div>
                </div>
                <p style={{ fontSize: 11, color: "#a0a8b4", margin: "8px 0 0", lineHeight: 1.4 }} className="mf-training-tip">
                  Stabilization fee: <strong style={{ color: "#0d9488" }}>(Market Rent × 3) + 20% = ${((Number(monthlyRent || 0) * 3) * 1.20 * (portfolioDiscount ? 0.85 : 1)).toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong>{portfolioDiscount ? " (portfolio discount −15%)" : ""} per unit. Scope-Lock Agreement embeds locked Before/After images + materials list + this fee.{linkedProjectId && <span style={{ color: "#A78BFA", display: "block", marginTop: 4 }}>✓ When generated, this agreement will auto-archive to Project <strong>{linkedProjectId}</strong>&apos;s Documents folder.</span>}
                </p>
                {emailStatus && (
                  <p data-testid="ds-email-status" style={{ fontSize: 12, color: emailStatus.startsWith("Sent") ? "#10b981" : "#ef4444", margin: "6px 0 0", fontWeight: 600 }}>
                    {emailStatus}
                  </p>
                )}
              </div>
              <div className="ds-pdf-actions">
                <button className="ds-pitch-btn" onClick={handleGeneratePitch} disabled={generatingPitch} data-testid="ds-pitch-btn">
                  {generatingPitch ? "Generating..." : "Download Pitch PDF"}
                </button>
                <button className="ds-signoff-btn" onClick={handleGenerateSignoff} disabled={generatingSignoff} data-testid="ds-signoff-btn">
                  {generatingSignoff ? "Generating..." : "Generate Scope-Lock Agreement"}
                </button>
                <button className="ds-signoff-btn" onClick={handleEmailSignoff} disabled={emailingSignoff} data-testid="ds-email-signoff-btn" style={{ background: "#0d9488", color: "#fff", borderColor: "#0d9488" }}>
                  {emailingSignoff ? "Sending..." : "Email to Client"}
                </button>
              </div>
              {/* PURGED (Feb 2, 2026): Legacy MFC-01…MFC-10 color palette
                  swatches + palette-detail card. Replaced with the
                  Dynamic Materials Builder — line-item procurement inputs
                  across 7 categories (Paint · Cabinets · Countertops ·
                  Flooring · Backsplash · Hardware) that
                  drive the Sequential Cascade Engine end-to-end. */}
              <DynamicMaterialsBuilder
                propertyAddress={propertyAddress}
                entityId={linkedProjectId}
                unitType={unitType}
                marketRent={monthlyRent}
              />
            </div>
          )}

          {/* MIX & MATCH TAB — PURGED (Feb 12, 2026) per Doctrine v3.0.
              Formerly consumed the hardcoded PALETTES array. Owner now
              enters materials line-by-line in Dynamic Materials Builder. */}

          {/* DESIGN SOLUTIONS TAB (P1 — 6 curated Solutions + per-slot photo editor) */}
          {activeTab === "solutions" && (
            <div className="ds-solutions-view" data-testid="ds-solutions-view">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <p className="ds-section-desc" style={{ marginBottom: 4 }}>
                    Moovin Forward&apos;s curated Design Solutions library — the six pre-composed looks owners pick between on their proposal page. Every Solution is Section 8 · HUD-VASH · Housing Coalition compliant.
                  </p>
                  <p style={{ color: "#7E92A8", fontSize: "0.78rem", margin: 0 }}>
                    Each Solution has 4 independent photo slots. Regenerate the ones you don&apos;t love — the others stay locked.
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="ds-open-gallery"
                  onClick={() => setGalleryOpen(true)}
                  style={{ background: "linear-gradient(90deg, #A78BFA, #5AC8D8)", color: "#062617", border: "none", padding: "0.65rem 1.1rem", borderRadius: 8, fontSize: "0.85rem", fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}
                >🖼️ Manage Photos</button>
              </div>

              {solutionsLoading && <p style={{ color: "#7E92A8", fontSize: "0.85rem" }}>Loading curated Solutions…</p>}

              <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                {solutions.map(s => (
                  <div key={s.id} data-testid={`ds-solution-${s.id}`} style={{ background: "rgba(15,20,30,0.5)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 12, overflow: "hidden" }}>
                    {s.gallery_urls?.cover ? (
                      <img
                        src={`${BACKEND_URL}${s.gallery_urls.cover}`}
                        alt={s.name}
                        style={{ width: "100%", height: 160, objectFit: "cover", background: "rgba(6,12,20,0.5)" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div style={{ height: 160, background: `linear-gradient(135deg, ${s.palette?.colors?.[0]?.hex || "#1A1A1A"}, ${s.palette?.colors?.[2]?.hex || "#333"})`, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.4)", fontSize: "0.7rem", letterSpacing: "0.1em", fontWeight: 700 }}>PREVIEW COMING SOON</div>
                    )}
                    <div style={{ padding: "0.95rem 1.05rem" }}>
                      <h3 style={{ color: "#F3F4F6", fontSize: "1.05rem", fontWeight: 800, margin: "0 0 4px" }}>{s.name}</h3>
                      <p style={{ color: "#A78BFA", fontSize: "0.78rem", margin: "0 0 8px", fontStyle: "italic" }}>{s.tagline}</p>
                      <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                        {(s.palette?.colors || []).map(c => (
                          <div key={c.role} title={c.label} style={{ flex: 1, aspectRatio: "3/1", background: c.hex, borderRadius: 4, border: "1px solid rgba(255,255,255,0.08)" }} />
                        ))}
                      </div>
                      <p style={{ color: "#ADBED1", fontSize: "0.78rem", lineHeight: 1.5, margin: "0 0 10px" }}>{s.narrative}</p>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {s.compliance?.section_8 && <span style={{ background: "rgba(0,220,120,0.10)", color: "#4ADE80", border: "1px solid rgba(0,220,120,0.3)", padding: "1px 6px", borderRadius: 999, fontSize: "0.6rem", fontWeight: 800 }}>✓ Sec 8</span>}
                          {s.compliance?.hud_vash && <span style={{ background: "rgba(0,220,120,0.10)", color: "#4ADE80", border: "1px solid rgba(0,220,120,0.3)", padding: "1px 6px", borderRadius: 999, fontSize: "0.6rem", fontWeight: 800 }}>✓ HUD-VASH</span>}
                          {s.compliance?.housing_coalition && <span style={{ background: "rgba(0,220,120,0.10)", color: "#4ADE80", border: "1px solid rgba(0,220,120,0.3)", padding: "1px 6px", borderRadius: 999, fontSize: "0.6rem", fontWeight: 800 }}>✓ Coalition</span>}
                        </div>
                        <span style={{ color: "#7E92A8", fontSize: "0.72rem", fontWeight: 700 }}>{s.modules?.length || 0} modules</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PROPERTY SPECS TAB */}
          {activeTab === "specs" && (
            <div className="ds-specs-view" data-testid="ds-specs-view">
              <p className="ds-section-desc">Enter the property&apos;s room counts and dimensions. This auto-calculates exact material quantities in the Budget tab.</p>
              <div className="ds-specs-grid">
                <div className="ds-specs-section">
                  <h3>Rooms</h3>
                  <div className="ds-spec-row">
                    <label>Bedrooms</label>
                    <input type="number" value={specs.bedrooms} onChange={e => updateSpec("bedrooms", e.target.value)} data-testid="ds-spec-bedrooms" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Bathrooms</label>
                    <input type="number" value={specs.bathrooms} onChange={e => updateSpec("bathrooms", e.target.value)} data-testid="ds-spec-bathrooms" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Ceiling Height (ft)</label>
                    <input type="number" value={specs.ceiling_height} onChange={e => updateSpec("ceiling_height", e.target.value)} data-testid="ds-spec-ceiling" />
                  </div>
                </div>
                <div className="ds-specs-section">
                  <h3>Square Footage</h3>
                  <div className="ds-spec-row">
                    <label>Kitchen (sq ft)</label>
                    <input type="number" value={specs.kitchen_sqft} onChange={e => updateSpec("kitchen_sqft", e.target.value)} data-testid="ds-spec-kitchen" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Living Area (sq ft)</label>
                    <input type="number" value={specs.living_sqft} onChange={e => updateSpec("living_sqft", e.target.value)} data-testid="ds-spec-living" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Per Bedroom (sq ft)</label>
                    <input type="number" value={specs.bedroom_sqft} onChange={e => updateSpec("bedroom_sqft", e.target.value)} data-testid="ds-spec-bedsqft" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Per Bathroom (sq ft)</label>
                    <input type="number" value={specs.bathroom_sqft} onChange={e => updateSpec("bathroom_sqft", e.target.value)} data-testid="ds-spec-bathsqft" />
                  </div>
                </div>
                <div className="ds-specs-section">
                  <h3>Fixtures & Hardware</h3>
                  <div className="ds-spec-row">
                    <label>Cabinet Doors</label>
                    <input type="number" value={specs.cabinet_doors} onChange={e => updateSpec("cabinet_doors", e.target.value)} data-testid="ds-spec-cabdoors" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Drawers</label>
                    <input type="number" value={specs.drawer_count} onChange={e => updateSpec("drawer_count", e.target.value)} data-testid="ds-spec-drawers" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Light Fixtures</label>
                    <input type="number" value={specs.light_fixtures} onChange={e => updateSpec("light_fixtures", e.target.value)} data-testid="ds-spec-lights" />
                  </div>
                  <div className="ds-spec-row">
                    <label>Sinks / Faucets</label>
                    <input type="number" value={specs.sinks} onChange={e => updateSpec("sinks", e.target.value)} data-testid="ds-spec-sinks" />
                  </div>
                </div>
              </div>

              {/* Calculated Preview */}
              <div className="ds-specs-preview" data-testid="ds-specs-preview">
                <h3>Calculated Material Needs</h3>
                <div className="ds-specs-calc-grid">
                  <div className="ds-specs-calc-item">
                    <span className="ds-specs-calc-val">{Math.ceil(((specs.kitchen_sqft + specs.living_sqft + specs.bedroom_sqft * specs.bedrooms + specs.bathroom_sqft * specs.bathrooms) * 0.4 * specs.ceiling_height * 2 * 2) / 350) + 1}</span>
                    <span className="ds-specs-calc-lbl">Gallons Paint</span>
                  </div>
                  <div className="ds-specs-calc-item">
                    <span className="ds-specs-calc-val">{Math.ceil(((specs.kitchen_sqft + specs.living_sqft + specs.bedroom_sqft * specs.bedrooms + specs.bathroom_sqft * specs.bathrooms) * 1.1) / 24) + 1}</span>
                    <span className="ds-specs-calc-lbl">Boxes Flooring</span>
                  </div>
                  <div className="ds-specs-calc-item">
                    <span className="ds-specs-calc-val">{Math.round(specs.bathroom_sqft * specs.bathrooms * 0.6 * 1.1)}</span>
                    <span className="ds-specs-calc-lbl">Sq Ft Tile</span>
                  </div>
                  <div className="ds-specs-calc-item">
                    <span className="ds-specs-calc-val">{specs.cabinet_doors + specs.drawer_count}</span>
                    <span className="ds-specs-calc-lbl">Hardware Pulls</span>
                  </div>
                  <div className="ds-specs-calc-item">
                    <span className="ds-specs-calc-val">{specs.bathrooms}</span>
                    <span className="ds-specs-calc-lbl">Vanities</span>
                  </div>
                  <div className="ds-specs-calc-item">
                    <span className="ds-specs-calc-val">{specs.light_fixtures}</span>
                    <span className="ds-specs-calc-lbl">Light Fixtures</span>
                  </div>
                </div>
                <button className="ds-auto-populate-btn" onClick={autoPopulateBudget} data-testid="ds-auto-populate">
                  Send to Budget Calculator
                </button>
              </div>
            </div>
          )}

          {/* SWIFT TURN-KEY TAB */}
          {activeTab === "swift" && (
            <div className="ds-swift-view" data-testid="ds-swift-view">
              <p className="ds-section-desc">Peel-and-stick, interchangeable finishes engineered for rapid turnover. Install fast. Swap faster.</p>
              <div className="ds-swift-grid">
                {PEEL_STICK_OPTIONS.map((opt, i) => (
                  <div key={i} className="ds-swift-card" data-testid={`ds-swift-${i}`}>
                    <div className="ds-swift-tag">{opt.tag}</div>
                    <h4>{opt.name}</h4>
                    <p>{opt.desc}</p>
                    {opt.shop && <ShopBadge />}
                  </div>
                ))}
              </div>
              <div className="ds-swift-note">
                <h4>Why Peel & Stick?</h4>
                <div className="ds-swift-benefits">
                  <div className="ds-benefit">
                    <span className="ds-benefit-val">2-4 hrs</span>
                    <span className="ds-benefit-lbl">Full floor install per unit</span>
                  </div>
                  <div className="ds-benefit">
                    <span className="ds-benefit-val">30 min</span>
                    <span className="ds-benefit-lbl">Backsplash refresh</span>
                  </div>
                  <div className="ds-benefit">
                    <span className="ds-benefit-val">$0</span>
                    <span className="ds-benefit-lbl">Special tools needed</span>
                  </div>
                  <div className="ds-benefit">
                    <span className="ds-benefit-val">100%</span>
                    <span className="ds-benefit-lbl">Renter-grade durable</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NSPIRE TAB — guided room-by-room walkthrough (Feb 2, 2026).
              Attached to the Design Studio render + procurement flow via
              property_address; auto-populates the corporate Resolute
              closeout packet for each institutional entity. */}
          {activeTab === "nspire" && (
            <div className="ds-nspire-view" data-testid="ds-nspire-view">
              <p className="ds-section-desc">
                Room-by-room NSPIRE / Section 8 walkthrough. Attach photos + notes per deficiency; results auto-populate the corporate closeout packet.
              </p>
              <NspireWalkthrough
                propertyAddress={propertyAddress}
                entityId={linkedProjectId}
                inspector={authedUser?.name || ""}
              />
            </div>
          )}

          {/* BUDGET TAB — Secret Sauce */}
          {activeTab === "budget" && (
            <div className="ds-budget-view" data-testid="ds-budget-view">
              <div className="ds-budget-warning">
                <span className="ds-budget-lock">&#128274;</span>
                Internal use only — this data never appears on client-facing PDFs or proposals.
              </div>
              <div className="ds-budget-table" data-testid="ds-budget-table">
                <div className="ds-budget-header-row">
                  <span className="ds-bh-material">Material</span>
                  <span className="ds-bh">Coverage</span>
                  <span className="ds-bh">Sq Ft / Qty</span>
                  <span className="ds-bh">Units Needed</span>
                  <span className="ds-bh">Cost / Unit</span>
                  <span className="ds-bh">Line Total</span>
                </div>
                {MATERIAL_SPECS.map(spec => {
                  const item = budgetItems[spec.key] || {};
                  const sqft = item.sqft || 0;
                  const costPerUnit = item.costPerUnit || 0;
                  const qty = calcQty(spec, sqft);
                  const lineTotal = qty * costPerUnit;
                  return (
                    <div key={spec.key} className="ds-budget-row" data-testid={`ds-budget-${spec.key}`}>
                      <div className="ds-budget-material">
                        <span className="ds-budget-mat-name">{spec.label}</span>
                        <span className="ds-budget-mat-note">{spec.note}</span>
                      </div>
                      <div className="ds-budget-cell">
                        <span className="ds-budget-coverage">{spec.coveragePerUnit} {spec.coverageLabel}</span>
                      </div>
                      <div className="ds-budget-cell">
                        <input
                          type="number"
                          className="ds-budget-input"
                          placeholder="0"
                          value={item.sqft || ""}
                          onChange={e => updateBudget(spec.key, "sqft", Number(e.target.value) || 0)}
                          data-testid={`ds-budget-sqft-${spec.key}`}
                        />
                      </div>
                      <div className="ds-budget-cell">
                        <span className={`ds-budget-qty ${qty > 0 ? "ds-budget-qty-active" : ""}`}>
                          {qty} {spec.unit === "box" ? (qty !== 1 ? "boxes" : "box") : `${spec.unit}${qty !== 1 ? "s" : ""}`}
                        </span>
                      </div>
                      <div className="ds-budget-cell">
                        <div className="ds-budget-cost-wrap">
                          <span className="ds-budget-dollar">$</span>
                          <input
                            type="number"
                            className="ds-budget-input ds-budget-cost-input"
                            placeholder="0"
                            value={item.costPerUnit || ""}
                            onChange={e => updateBudget(spec.key, "costPerUnit", Number(e.target.value) || 0)}
                            data-testid={`ds-budget-cost-${spec.key}`}
                          />
                        </div>
                      </div>
                      <div className="ds-budget-cell">
                        <span className={`ds-budget-line-total ${lineTotal > 0 ? "ds-budget-total-active" : ""}`}>
                          ${lineTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div className="ds-budget-grand-total" data-testid="ds-budget-grand-total">
                  <span>Total Material Budget</span>
                  <span className="ds-budget-grand-val">${getBudgetTotal().toLocaleString()}</span>
                </div>
                <div data-testid="ds-unit-budget-cap" className="mf-training-tip" style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: budgetOver ? "rgba(239, 68, 68, 0.10)" : "rgba(13, 148, 136, 0.08)", border: `1px solid ${budgetOver ? "rgba(239, 68, 68, 0.45)" : "rgba(13, 148, 136, 0.35)"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, fontWeight: 600, color: budgetOver ? "#ef4444" : "#0d9488" }}>
                    <span>{unitType.replace("bed", "BR").replace("studio", "Studio")} Unit Cap: ${currentCap.toLocaleString()}</span>
                    <span>{budgetPct}% used{budgetOver ? " — OVER CAP" : ""}</span>
                  </div>
                  <div style={{ height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 3, marginTop: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${budgetPct}%`, background: budgetOver ? "#ef4444" : "#0d9488", transition: "width 0.25s ease" }}></div>
                  </div>
                  <p style={{ fontSize: 10.5, color: "#94a3b8", margin: "6px 0 0", lineHeight: 1.4 }}>
                    Internal target cap for materials only (excludes stabilization fee). Bulk procurement via DFW wholesalers (Seconds &amp; Surplus, Floor City, iDeal Floors, ProSource, Legacy Builder Supply) keeps you under cap — volume savings stay internal, client sees locked per-unit material cost.
                  </p>
                </div>
              </div>
            </div>
          )}


          {/* MATERIALS CATALOG TAB */}
          {activeTab === "materials" && (
            <div className="ds-materials-catalog" data-testid="ds-materials-catalog">
              <p className="ds-section-desc">Approved vendor products we use across all stabilization projects. These are the actual materials that go into every unit.</p>
              {VENDOR_CATALOG.map(vendor => (
                <div key={vendor.vendor} className="ds-vendor-section" data-testid={`ds-vendor-${vendor.vendor.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="ds-vendor-header" style={{ borderLeftColor: vendor.accentColor }}>
                    <div className="ds-vendor-logo" style={{ background: vendor.accentColor }}>{vendor.logo}</div>
                    <div className="ds-vendor-info">
                      <h3>{vendor.vendor}</h3>
                      <span className="ds-vendor-account">{vendor.account}</span>
                    </div>
                  </div>
                  <div className="ds-vendor-products">
                    {vendor.products.map((product, i) => (
                      <div key={i} className="ds-vendor-product" data-testid={`ds-product-${vendor.vendor.replace(/\s+/g, "-").toLowerCase()}-${i}`}>
                        <div className="ds-product-top">
                          <span className="ds-product-category" style={{ color: vendor.accentColor }}>{product.category}</span>
                          <span className="ds-product-finish">{product.finish}</span>
                        </div>
                        <h4 className="ds-product-name">{product.name}</h4>
                        <p className="ds-product-use">{product.use}</p>
                        <div className="ds-product-meta">
                          <span className="ds-product-coverage">{product.coverage}</span>
                          <span className="ds-product-note">{product.note}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="ds-vendor-disclaimer">
                All vendor accounts provide commercial/contractor pricing. Product availability and pricing may vary by location. Material selections for specific units are finalized during the walk-through and scope phase.
              </div>
            </div>
          )}


          {/* SUMMARY TAB — PURGED (Feb 12, 2026) per Doctrine v3.0.
              Formerly rendered a recap of PALETTES-driven Mix & Match
              selections. Procurement manifest + finish spec now live
              inside Dynamic Materials Builder + Budget tab. */}

          {/* FIELD MODE — Embedded inline (Feb 2, 2026).
              Loads the full Field Mode operational panel inside the
              Design Studio workspace so crews can move from procurement
              spec → on-site tally / photo capture without leaving page
              context. Same offline queue + PWA cache. */}
          {activeTab === "field-inline" && (
            <div data-testid="ds-field-inline" style={{ padding: 0, margin: "0 -1rem" }}>
              <div style={{ background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.15)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.18em", fontWeight: 800 }}>
                  &gt; FIELD MODE · EMBEDDED IN DESIGN STUDIO
                </div>
                <div style={{ color: "#B4BEC8", fontSize: "0.78rem", marginTop: 3 }}>
                  Crew ops without leaving the workspace — offline photo capture, tally items, end-of-shift PDF. All state persists to the same session.
                </div>
              </div>
              <FieldMode embedded={true} />
            </div>
          )}

          {/* MEMORY BOOK TAB */}
          {activeTab === "memory" && (
            <div className="ds-memory-view" data-testid="ds-memory-view" style={{ padding: "0.5rem 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <h2 style={{ color: "#E6ECF2", fontSize: "1.3rem", margin: "0 0 0.3rem", fontWeight: 800 }}>
                    <i className="fas fa-bookmark" style={{ color: "#E8B8C4", marginRight: 8 }}></i>
                    Memory Book
                  </h2>
                  <p style={{ color: "#ADBED1", fontSize: "0.85rem", margin: 0, lineHeight: 1.5 }}>
                    Your private portfolio gallery — every saved render, sourced product list, and total cost. Click any card to open the full spec sheet or download a printable PDF.
                  </p>
                </div>
                <button type="button" onClick={loadMemoryBook} disabled={mbLoading} data-testid="ds-memory-refresh" style={{ background: "transparent", border: "1px solid rgba(167,139,250,0.35)", color: "#A78BFA", padding: "0.45rem 0.85rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
                  <i className="fas fa-rotate" style={{ marginRight: 5 }}></i>
                  {mbLoading ? "Loading..." : "Refresh"}
                </button>
              </div>

              {mbLoading && mbEntries.length === 0 ? (
                <p style={{ color: "#7E92A8", fontSize: "0.85rem", textAlign: "center", padding: "2rem 0" }}>Loading your Memory Book...</p>
              ) : mbEntries.length === 0 ? (
                <div data-testid="ds-memory-empty" style={{ textAlign: "center", padding: "3rem 1rem", background: "rgba(167,139,250,0.05)", border: "1px dashed rgba(167,139,250,0.25)", borderRadius: 12 }}>
                  <i className="fas fa-bookmark" style={{ fontSize: "2rem", color: "#A78BFA", opacity: 0.5, marginBottom: 12 }}></i>
                  <h3 style={{ color: "#E6ECF2", fontSize: "1rem", margin: "0 0 0.4rem", fontWeight: 700 }}>No saved renders yet</h3>
                  <p style={{ color: "#ADBED1", fontSize: "0.8rem", margin: 0, lineHeight: 1.5 }}>
                    Generate a visualization above, then click <strong style={{ color: "#E8B8C4" }}>Save to Memory Book</strong> on the AFTER image.
                  </p>
                </div>
              ) : (
                <div data-testid="ds-memory-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
                  {mbEntries.map((e) => (
                    <div
                      key={e.entry_id}
                      data-testid={`ds-memory-card-${e.entry_id}`}
                      onClick={() => setMbDetail(e)}
                      style={{
                        background: "rgba(15,20,30,0.7)",
                        border: "1px solid rgba(167,139,250,0.22)",
                        borderRadius: 12,
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "transform 0.15s, border-color 0.15s",
                      }}
                      onMouseEnter={(ev) => { ev.currentTarget.style.transform = "translateY(-2px)"; ev.currentTarget.style.borderColor = "rgba(232,184,196,0.5)"; }}
                      onMouseLeave={(ev) => { ev.currentTarget.style.transform = "translateY(0)"; ev.currentTarget.style.borderColor = "rgba(167,139,250,0.22)"; }}
                    >
                      {e.after_image_b64 ? (
                        <img src={`data:image/png;base64,${e.after_image_b64}`} alt="" style={{ width: "100%", height: 160, objectFit: "cover", display: "block", background: "#1a1f2e" }} />
                      ) : (
                        <div style={{ height: 160, background: "linear-gradient(135deg, rgba(232,184,196,0.2), rgba(167,139,250,0.2))", display: "flex", alignItems: "center", justifyContent: "center", color: "#A78BFA", fontSize: 36 }}>
                          <i className="fas fa-image"></i>
                        </div>
                      )}
                      <div style={{ padding: "0.8rem 0.9rem" }}>
                        <div style={{ fontSize: "0.9rem", color: "#E6ECF2", fontWeight: 700, marginBottom: 4, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {e.title || "Untitled"}
                        </div>
                        {e.subject_name && (
                          <div style={{ fontSize: "0.72rem", color: "#E8B8C4", marginBottom: 4, fontStyle: "italic" }}>
                            For {e.subject_name}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", fontSize: "0.7rem", color: "#ADBED1", marginBottom: 6 }}>
                          {e.palette_name && <span style={{ background: "rgba(167,139,250,0.15)", color: "#A78BFA", padding: "2px 7px", borderRadius: 999 }}>{e.palette_name}</span>}
                          {e.room_type && <span style={{ background: "rgba(0,200,200,0.12)", color: "#00C8C8", padding: "2px 7px", borderRadius: 999 }}>{e.room_type}</span>}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.75rem", color: "#7E92A8" }}>
                          <span>{(e.products || []).length} product{(e.products || []).length === 1 ? "" : "s"}</span>
                          <span style={{ color: "#0d9488", fontWeight: 700 }}>${Number(e.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Detail Modal */}
              {mbDetail && (
                <div data-testid="ds-memory-detail-modal" onClick={() => setMbDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}>
                  <div onClick={(e) => e.stopPropagation()} style={{ background: "#0f1420", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 14, maxWidth: 920, width: "100%", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
                    <div style={{ padding: "1.2rem 1.5rem", borderBottom: "1px solid rgba(167,139,250,0.15)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, position: "sticky", top: 0, background: "#0f1420", zIndex: 2 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ color: "#E6ECF2", fontSize: "1.15rem", margin: "0 0 0.3rem", fontWeight: 800 }}>{mbDetail.title}</h3>
                        <div style={{ display: "flex", gap: 8, fontSize: "0.74rem", color: "#ADBED1", flexWrap: "wrap" }}>
                          {mbDetail.subject_name && <span>For <strong style={{ color: "#E8B8C4" }}>{mbDetail.subject_name}</strong></span>}
                          {mbDetail.palette_name && <span style={{ color: "#A78BFA" }}>{mbDetail.palette_name}</span>}
                          {mbDetail.room_type && <span style={{ color: "#00C8C8" }}>{mbDetail.room_type}</span>}
                          {mbDetail.created_at && <span>{new Date(mbDetail.created_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <button type="button" onClick={() => setMbDetail(null)} style={{ background: "transparent", border: "none", color: "#ADBED1", fontSize: "1.2rem", cursor: "pointer" }}><i className="fas fa-xmark"></i></button>
                    </div>

                    <div style={{ padding: "1.2rem 1.5rem" }}>
                      {(mbDetail.tags || []).length > 0 && (
                        <div style={{ marginBottom: "1rem", display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {mbDetail.tags.map((t, i) => (
                            <span key={i} style={{ background: "rgba(167,139,250,0.12)", color: "#A78BFA", padding: "3px 9px", borderRadius: 999, fontSize: "0.72rem" }}>#{t}</span>
                          ))}
                        </div>
                      )}

                      {/* Before / After side by side */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: "1.2rem" }}>
                        <div>
                          <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#7E92A8", marginBottom: 4, letterSpacing: 1 }}>BEFORE</div>
                          {mbDetail.before_image_b64 ? (
                            <img src={`data:image/png;base64,${mbDetail.before_image_b64}`} alt="Before" style={{ width: "100%", borderRadius: 8, display: "block" }} />
                          ) : <div style={{ aspectRatio: "4/3", background: "rgba(255,255,255,0.04)", borderRadius: 8 }}></div>}
                        </div>
                        <div>
                          <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#E8B8C4", marginBottom: 4, letterSpacing: 1 }}>AFTER</div>
                          {mbDetail.after_image_b64 ? (
                            <img src={`data:image/png;base64,${mbDetail.after_image_b64}`} alt="After" style={{ width: "100%", borderRadius: 8, display: "block" }} />
                          ) : <div style={{ aspectRatio: "4/3", background: "rgba(255,255,255,0.04)", borderRadius: 8 }}></div>}
                        </div>
                      </div>

                      {mbDetail.notes && (
                        <div style={{ background: "rgba(232,184,196,0.05)", border: "1px solid rgba(232,184,196,0.2)", borderRadius: 8, padding: "0.7rem 0.9rem", marginBottom: "1rem" }}>
                          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#E8B8C4", marginBottom: 4, letterSpacing: 0.5 }}>THE STORY</div>
                          <p style={{ color: "#E6ECF2", fontSize: "0.85rem", margin: 0, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{mbDetail.notes}</p>
                        </div>
                      )}

                      {(mbDetail.products || []).length > 0 && (
                        <div style={{ marginBottom: "1rem" }}>
                          <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#E6ECF2", marginBottom: 8 }}>
                            <i className="fas fa-box" style={{ color: "#A78BFA", marginRight: 6 }}></i>Sourced Products
                          </div>
                          <div style={{ background: "rgba(15,20,30,0.5)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: 8, overflow: "hidden" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 50px 90px 90px", gap: 8, padding: "8px 12px", background: "rgba(167,139,250,0.08)", fontSize: "0.7rem", color: "#A78BFA", fontWeight: 700, letterSpacing: 0.5 }}>
                              <span>PRODUCT</span><span style={{ textAlign: "center" }}>QTY</span><span style={{ textAlign: "right" }}>UNIT</span><span style={{ textAlign: "right" }}>LINE</span>
                            </div>
                            {mbDetail.products.map((p, i) => (
                              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 50px 90px 90px", gap: 8, padding: "8px 12px", borderTop: i === 0 ? "none" : "1px solid rgba(167,139,250,0.08)", fontSize: "0.78rem", color: "#E6ECF2", alignItems: "center" }}>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.title}>
                                  {p.source_url ? (
                                    <a href={p.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "#E6ECF2", textDecoration: "none" }}>{p.title}</a>
                                  ) : p.title}
                                </span>
                                <span style={{ textAlign: "center", color: "#ADBED1" }}>{p.quantity || 1}</span>
                                <span style={{ textAlign: "right", color: "#ADBED1" }}>${Number(p.price || 0).toFixed(2)}</span>
                                <span style={{ textAlign: "right", color: "#0d9488", fontWeight: 700 }}>${Number(p.line_total || 0).toFixed(2)}</span>
                              </div>
                            ))}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 50px 90px 90px", gap: 8, padding: "10px 12px", borderTop: "1px dashed rgba(167,139,250,0.3)", fontSize: "0.95rem", color: "#0d9488", fontWeight: 800, background: "rgba(13,148,136,0.06)" }}>
                              <span></span><span></span><span style={{ textAlign: "right" }}>Total</span><span style={{ textAlign: "right" }}>${Number(mbDetail.total_cost || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", paddingTop: "0.8rem", borderTop: "1px solid rgba(167,139,250,0.12)" }}>
                        <button type="button" onClick={() => deleteMemoryBookEntry(mbDetail.entry_id)} data-testid="ds-memory-delete" style={{ background: "transparent", border: "1px solid rgba(239,68,68,0.4)", color: "#ef4444", padding: "0.5rem 0.95rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
                          <i className="fas fa-trash" style={{ marginRight: 5 }}></i>Delete
                        </button>
                        <button type="button" onClick={() => downloadMemoryBookPdf(mbDetail.entry_id, mbDetail.title)} disabled={mbPdfBusy} data-testid="ds-memory-pdf" style={{ background: "linear-gradient(135deg, #E8B8C4 0%, #A78BFA 100%)", border: "none", color: "#1a1a2e", padding: "0.5rem 1.1rem", borderRadius: 8, fontSize: "0.82rem", fontWeight: 800, cursor: mbPdfBusy ? "wait" : "pointer" }}>
                          <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>{mbPdfBusy ? "Building..." : "Memory Book PDF"}
                        </button>
                      </div>

                      {/* Landlord Proposal Panel — Calculator + PDF + Email */}
                      <div data-testid="ds-memory-proposal-panel" style={{ marginTop: "1.2rem", background: "linear-gradient(135deg, rgba(13,148,136,0.06) 0%, rgba(232,184,196,0.04) 100%)", border: "1px solid rgba(13,148,136,0.25)", borderRadius: 12, padding: "1rem 1.1rem" }}>
                        <h4 style={{ color: "#0d9488", fontSize: "0.95rem", margin: "0 0 0.4rem", fontWeight: 800 }}>
                          <i className="fas fa-calculator" style={{ marginRight: 6 }}></i>
                          Build Landlord Flat-Fee Proposal
                        </h4>
                        <p style={{ color: "#ADBED1", fontSize: "0.76rem", margin: "0 0 0.8rem", lineHeight: 1.5 }}>
                          Sized by <strong style={{ color: "#0d9488" }}>actual room square footage</strong>, not unit-type buckets. Estimate from photo, type it in, or pull from the linked project — system does the math.
                        </p>

                        {/* Linked-project room picker (Boom 1) */}
                        {mbLinkedRooms.length > 0 && (
                          <div data-testid="ds-mb-linked-rooms" style={{ background: "rgba(0,200,200,0.07)", border: "1px solid rgba(0,200,200,0.25)", borderRadius: 8, padding: "0.55rem 0.75rem", marginBottom: 10 }}>
                            <div style={{ fontSize: "0.72rem", color: "#00C8C8", fontWeight: 700, marginBottom: 5 }}>
                              <i className="fas fa-link" style={{ marginRight: 5 }}></i>From linked project — tap to use
                            </div>
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                              {mbLinkedRooms.map((r, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => { setMbRoomSqft(String(r.sqft)); setMbEstimateInfo(null); }}
                                  data-testid={`ds-mb-linked-room-${i}`}
                                  style={{ background: "rgba(0,200,200,0.15)", border: "1px solid rgba(0,200,200,0.35)", color: "#00C8C8", padding: "3px 8px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 600, cursor: "pointer" }}
                                >
                                  {r.name} · {r.sqft} sqft
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Room sqft + Estimate from photo (Boom 2+3) */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "end", marginBottom: 8 }}>
                          <div>
                            <label style={{ fontSize: "0.7rem", color: "#A78BFA", fontWeight: 700, display: "block", marginBottom: 3 }}>
                              Room Size <span style={{ color: "#7E92A8", fontWeight: 400 }}>(square feet)</span>
                            </label>
                            <input
                              type="number"
                              min="20"
                              max="3000"
                              step="1"
                              value={mbRoomSqft}
                              onChange={(e) => { setMbRoomSqft(e.target.value); }}
                              placeholder="e.g., 132 for a small kid's bedroom"
                              data-testid="ds-mb-room-sqft"
                              style={{ width: "100%", background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "0.45rem 0.6rem", color: "#E6ECF2", fontSize: "0.85rem", fontWeight: 700 }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={estimateRoomSize}
                            disabled={mbEstimating}
                            data-testid="ds-mb-estimate-photo"
                            title="Use Gemini Vision to estimate from the photo"
                            style={{
                              background: "linear-gradient(135deg, #A78BFA 0%, #00C8C8 100%)",
                              border: "none",
                              color: "#1a1a2e",
                              padding: "0.5rem 0.9rem",
                              borderRadius: 8,
                              fontSize: "0.78rem",
                              fontWeight: 800,
                              cursor: mbEstimating ? "wait" : "pointer",
                              whiteSpace: "nowrap"
                            }}
                          >
                            <i className="fas fa-wand-magic-sparkles" style={{ marginRight: 5 }}></i>
                            {mbEstimating ? "Estimating..." : "Estimate from Photo"}
                          </button>
                        </div>

                        {/* Estimate result hint */}
                        {mbEstimateInfo && (
                          <div data-testid="ds-mb-estimate-info" style={{ background: mbEstimateInfo.ok ? "rgba(0,200,200,0.07)" : "rgba(239,68,68,0.08)", border: `1px solid ${mbEstimateInfo.ok ? "rgba(0,200,200,0.3)" : "rgba(239,68,68,0.3)"}`, borderRadius: 6, padding: "0.5rem 0.65rem", marginBottom: 8, fontSize: "0.72rem" }}>
                            {mbEstimateInfo.ok ? (
                              <>
                                <span style={{ color: "#00C8C8", fontWeight: 700 }}>
                                  <i className="fas fa-circle-check" style={{ marginRight: 4 }}></i>
                                  {mbEstimateInfo.estimated_sqft} sqft estimated · {mbEstimateInfo.confidence} confidence
                                </span>
                                {mbEstimateInfo.reasoning && (
                                  <div style={{ color: "#ADBED1", marginTop: 3, fontStyle: "italic" }}>{mbEstimateInfo.reasoning}</div>
                                )}
                                {mbEstimateInfo.ceiling_note && (
                                  <div style={{ color: "#7E92A8", marginTop: 2 }}>{mbEstimateInfo.ceiling_note}</div>
                                )}
                              </>
                            ) : (
                              <span style={{ color: "#ef4444" }}>
                                <i className="fas fa-triangle-exclamation" style={{ marginRight: 4 }}></i>
                                {mbEstimateInfo.error}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Scope Tier + Cycle */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "end", marginBottom: 10 }}>
                          <div>
                            <label style={{ fontSize: "0.7rem", color: "#A78BFA", fontWeight: 700, display: "block", marginBottom: 3 }}>Scope Tier</label>
                            <select value={mbScopeTier} onChange={(e) => { setMbScopeTier(e.target.value); }} data-testid="ds-mb-scope-tier" style={{ width: "100%", background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "0.4rem 0.55rem", color: "#E6ECF2", fontSize: "0.8rem" }}>
                              <option value="accent_wall_only">Accent Wall Only (reversible)</option>
                              <option value="full_refresh">Full Single-Room Refresh</option>
                              <option value="statement_room">Statement Room (integrated feature)</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: "0.7rem", color: "#A78BFA", fontWeight: 700, display: "block", marginBottom: 3 }}>Cycle</label>
                            <select value={mbCalcCycle} onChange={(e) => { setMbCalcCycle(e.target.value); }} data-testid="ds-mb-cycle" style={{ width: "100%", background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "0.4rem 0.55rem", color: "#E6ECF2", fontSize: "0.8rem" }}>
                              <option value="swift_48hr">48-Hour Swift (+35%)</option>
                              <option value="standard_14d">14-Day Standard</option>
                              <option value="heavy_21d">21-Day Heavy (+15%)</option>
                            </select>
                          </div>
                          <button type="button" onClick={runMemoryBookCalculator} disabled={mbCalcBusy} data-testid="ds-mb-calc-run" style={{ background: "#0d9488", border: "none", color: "#fff", padding: "0.5rem 1rem", borderRadius: 6, fontSize: "0.8rem", fontWeight: 700, cursor: mbCalcBusy ? "wait" : "pointer", whiteSpace: "nowrap" }}>
                            {mbCalcBusy ? "Calculating…" : "↻ Recalc"}
                          </button>
                        </div>

                        {/* Interchangeable Wall Treatments picker */}
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: "0.7rem", color: "#A78BFA", fontWeight: 700, display: "block", marginBottom: 5 }}>
                            Interchangeable Wall Treatments <span style={{ color: "#7E92A8", fontWeight: 400 }}>(reversible — no paint)</span>
                          </label>
                          <div data-testid="ds-mb-wall-treatments" style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                            {[
                              { id: "peel_stick_wallpaper", label: "Peel-Stick Wallpaper" },
                              { id: "3d_pvc_panels", label: "3D PVC Panels" },
                              { id: "fluted_slat_panels", label: "Fluted Slat Panels" },
                              { id: "removable_fabric", label: "Removable Fabric Panels" },
                              { id: "magnetic_boards", label: "Magnetic Accent Boards" },
                              { id: "command_strip_art", label: "Command-Strip Art Wall" },
                            ].map(t => {
                              const active = mbWallTreatments.includes(t.label);
                              return (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => {
                                    setMbWallTreatments(prev => active ? prev.filter(x => x !== t.label) : [...prev, t.label]);
                                  }}
                                  data-testid={`ds-mb-wall-${t.id}`}
                                  style={{
                                    background: active ? "linear-gradient(135deg, #E8B8C4 0%, #A78BFA 100%)" : "rgba(167,139,250,0.08)",
                                    border: `1px solid ${active ? "rgba(232,184,196,0.6)" : "rgba(167,139,250,0.25)"}`,
                                    color: active ? "#1a1a2e" : "#A78BFA",
                                    padding: "4px 10px",
                                    borderRadius: 999,
                                    fontSize: "0.72rem",
                                    fontWeight: active ? 800 : 600,
                                    cursor: "pointer",
                                    transition: "all 0.15s"
                                  }}
                                >
                                  {active && <i className="fas fa-check" style={{ marginRight: 4 }}></i>}
                                  {t.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Calculator results */}
                        {mbCalc && (
                          <div data-testid="ds-mb-calc-result" style={{ background: "rgba(15,20,30,0.5)", border: "1px solid rgba(13,148,136,0.25)", borderRadius: 8, padding: "0.75rem 0.9rem", marginBottom: 10 }}>
                            <div style={{ fontSize: "1.4rem", color: "#0d9488", fontWeight: 800, marginBottom: 6 }}>
                              ${Number(mbCalc.final_flat_fee ?? mbCalc.flat_fee ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              <span style={{ fontSize: "0.7rem", color: "#7E92A8", fontWeight: 500, marginLeft: 8 }}>locked flat fee</span>
                              {mbCalc.breakdown?.room_sqft && (
                                <span style={{ fontSize: "0.7rem", color: "#A78BFA", fontWeight: 600, marginLeft: 8 }}>· {mbCalc.breakdown.room_sqft} sqft · {mbCalc.breakdown.scope_tier_label}</span>
                              )}
                            </div>
                            {mbCalc.seasonal && (
                              <div data-testid="ds-mb-seasonal-badge" style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                background: mbCalc.seasonal.season === "peak" ? "rgba(245,158,11,0.15)" : "rgba(59,130,246,0.15)",
                                border: `1px solid ${mbCalc.seasonal.season === "peak" ? "#F59E0B" : "#3B82F6"}`,
                                color: mbCalc.seasonal.season === "peak" ? "#FCD34D" : "#93C5FD",
                                padding: "3px 8px", borderRadius: 999,
                                fontSize: "0.7rem", fontWeight: 700, marginBottom: 8,
                              }}>
                                {mbCalc.seasonal.season === "peak" ? "☀️" : "❄️"} {mbCalc.seasonal.window} · seasonal uplift {mbCalc.seasonal.markup_label} (+${Number(mbCalc.seasonal.uplift_amount || 0).toFixed(2)})
                              </div>
                            )}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: "0.74rem", color: "#ADBED1" }}>
                              <span>Crew Labor</span>
                              <span style={{ textAlign: "right", color: "#E6ECF2", fontWeight: 600 }}>
                                ${Number(mbCalc.labor_total || 0).toFixed(2)}
                                <span style={{ color: "#7E92A8", fontWeight: 400, fontSize: "0.66rem", marginLeft: 4 }}>
                                  ({mbCalc.breakdown?.installer_hours}h+{mbCalc.breakdown?.cleaner_hours}h × cycle ×{mbCalc.breakdown?.cycle_multiplier})
                                </span>
                              </span>
                              <span>Materials <span style={{ color: "#7E92A8" }}>(${Number(mbCalc.breakdown?.materials_raw || 0).toFixed(2)} raw +{mbCalc.breakdown?.materials_waste_pct}% waste +{mbCalc.breakdown?.materials_markup_pct}% markup)</span></span>
                              <span style={{ textAlign: "right", color: "#E6ECF2", fontWeight: 600 }}>${Number(mbCalc.materials_total || 0).toFixed(2)}</span>
                              <span>Overhead <span style={{ color: "#7E92A8" }}>{mbCalc.breakdown?.calc_basis === "per_sqft_room_refresh" ? "(room-scaled)" : ""}</span></span>
                              <span style={{ textAlign: "right", color: "#E6ECF2", fontWeight: 600 }}>${Number(mbCalc.overhead || 0).toFixed(2)}</span>
                              <span>Target Margin (18%)</span>
                              <span style={{ textAlign: "right", color: "#E6ECF2", fontWeight: 600 }}>${Number(mbCalc.target_margin || 0).toFixed(2)}</span>
                              {mbCalc.seasonal && (
                                <>
                                  <span>Base flat fee <span style={{ color: "#7E92A8" }}>(pre-season)</span></span>
                                  <span style={{ textAlign: "right", color: "#E6ECF2", fontWeight: 600 }}>${Number(mbCalc.seasonal.base_flat_fee || 0).toFixed(2)}</span>
                                  <span>Seasonal uplift ({mbCalc.seasonal.markup_label})</span>
                                  <span style={{ textAlign: "right", color: mbCalc.seasonal.season === "peak" ? "#FCD34D" : "#93C5FD", fontWeight: 700 }}>+${Number(mbCalc.seasonal.uplift_amount || 0).toFixed(2)}</span>
                                </>
                              )}
                            </div>
                            {mbCalc.material_cap?.exceeds_historic_cap && (
                              <div style={{ marginTop: 8, padding: "6px 8px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, fontSize: "0.72rem", color: "#ef4444" }}>
                                ⚠ {mbCalc.material_cap.alert}
                              </div>
                            )}
                          </div>
                        )}

                        {/* TREC firewall violations */}
                        {mbTrecFindings.length > 0 && (
                          <div data-testid="ds-mb-trec-violations" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "0.7rem 0.9rem", marginBottom: 10 }}>
                            <div style={{ fontSize: "0.78rem", color: "#ef4444", fontWeight: 700, marginBottom: 6 }}>
                              <i className="fas fa-triangle-exclamation" style={{ marginRight: 5 }}></i>
                              TREC Firewall Blocked — fix this language before sending
                            </div>
                            {mbTrecFindings.slice(0, 5).map((f, i) => (
                              <div key={i} style={{ fontSize: "0.72rem", color: "#ADBED1", marginBottom: 3 }}>
                                • <strong style={{ color: "#ef4444" }}>&quot;{f.trigger}&quot;</strong> → use <em style={{ color: "#10b981" }}>&quot;{f.safe_replacement}&quot;</em>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Landlord contact fields (shared between PDF and Email) */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                          <input type="text" value={mbLandlordName} onChange={(e) => setMbLandlordName(e.target.value)} placeholder="Landlord name" data-testid="ds-mb-landlord-name" style={{ background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "0.4rem 0.55rem", color: "#E6ECF2", fontSize: "0.78rem" }} />
                          <input type="email" value={mbLandlordEmail} onChange={(e) => setMbLandlordEmail(e.target.value)} placeholder="landlord@email.com" data-testid="ds-mb-landlord-email" style={{ background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "0.4rem 0.55rem", color: "#E6ECF2", fontSize: "0.78rem" }} />
                        </div>
                        <input type="text" value={mbPropertyAddress} onChange={(e) => setMbPropertyAddress(e.target.value)} placeholder="Property address (optional)" data-testid="ds-mb-property-address" style={{ width: "100%", background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "0.4rem 0.55rem", color: "#E6ECF2", fontSize: "0.78rem", marginBottom: 8 }} />
                        <textarea value={mbCustomMessage} onChange={(e) => setMbCustomMessage(e.target.value)} rows={2} placeholder="Custom message (optional — speaks in your Pilot Program voice)" data-testid="ds-mb-custom-message" style={{ width: "100%", background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "0.4rem 0.55rem", color: "#E6ECF2", fontSize: "0.78rem", fontFamily: "inherit", resize: "vertical", marginBottom: 8 }} />

                        {mbEmailStatus && (
                          <p data-testid="ds-mb-email-status" style={{ fontSize: "0.75rem", color: mbEmailStatus.startsWith("✓") ? "#10b981" : "#ef4444", margin: "0 0 8px", fontWeight: 600 }}>{mbEmailStatus}</p>
                        )}

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <button type="button" onClick={downloadProposalPdf} disabled={mbProposalBusy} data-testid="ds-mb-proposal-pdf" style={{ background: "transparent", border: "1px solid rgba(13,148,136,0.5)", color: "#0d9488", padding: "0.5rem 0.95rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700, cursor: mbProposalBusy ? "wait" : "pointer" }}>
                            <i className="fas fa-file-arrow-down" style={{ marginRight: 5 }}></i>{mbProposalBusy ? "Building..." : "Download Proposal PDF"}
                          </button>
                          <button type="button" onClick={emailProposalToLandlord} disabled={mbEmailBusy || !mbLandlordEmail.trim()} data-testid="ds-mb-email-send" style={{ background: "linear-gradient(135deg, #0d9488 0%, #00C8C8 100%)", border: "none", color: "#fff", padding: "0.5rem 1.1rem", borderRadius: 8, fontSize: "0.82rem", fontWeight: 800, cursor: (mbEmailBusy || !mbLandlordEmail.trim()) ? "not-allowed" : "pointer", opacity: !mbLandlordEmail.trim() ? 0.5 : 1 }}>
                            <i className="fas fa-paper-plane" style={{ marginRight: 6 }}></i>{mbEmailBusy ? "Sending..." : "Email to Landlord"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
      </>
      )}
      <DesignGalleryModal open={galleryOpen} onClose={() => setGalleryOpen(false)} />
    </div>
  );
}
